import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from "react";
import { MilestoneProgress } from "./MilestoneProgress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowRight,
  ArrowLeft,
  CalendarIcon,
  ChevronDown,
  Lightbulb,
  Camera,
  Upload,
  FileImage,
  X,
  CheckCircle2,
  FileText,
  AlertTriangle,
  HelpCircle,
  ScanLine,
  Plus,
  Trash2,
  Pencil,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { scanDocument, estEchecTechnique } from "@/lib/document-ocr";
import { FlowData, TravelEntry } from "@/types/unified-flow";
import {
  Question,
  QUESTIONS,
  QUESTION_ORDER,
  getEstimatedTimeRemaining,
  COUNTRIES,
  MultiEntryFieldDef,
} from "@/data/questions";
import { getDemandeQuestions } from "@/data/demande-questions";
import { getUiTranslations } from "@/lib/ui-translations";
import { premiereLignePreRemplie } from "@/lib/prefill-premiere-ligne";
import { getDateFnsLocale, formatDateLocalized } from "@/lib/date-locale";
import { getLocalizedOptionLabel } from "@/lib/option-labels";
// DB-sourced questions override the static defaults when provided
import {
  TravelHistoryInput,
  formatTravelHistoryForReview,
} from "./TravelHistoryInput";
import { FeedbackScreen, type FeedbackData } from "./FeedbackScreen";
import { CountryCombobox } from "./CountryCombobox";
import { PhoneCountrySelect } from "./PhoneCountrySelect";
import { AddressAutocomplete } from "./AddressAutocomplete";
import { LanguageSwitcher } from "./LanguageSwitcher";
import type { SupportedLanguage } from "@/lib/ui-translations";
import { useDocumentOcr } from "@/hooks/useDocumentOcr";
import { useConvexDocumentOcrConfigs } from "@/lib/document-ocr-from-convex";
import { OcrContext } from "@/lib/convex";
import type { SaveStatus } from "@/pages/Index";
import {
  isRepeatGroup,
  isPersonGroup,
  rosterFields,
  expandGroup,
  ensureRowIds,
  readAnswer,
  writeAnswer,
  parseVirtualId,
  newRowId,
  isFlatVirtual,
  personOcrConfig,
  mergeAnswers,
  pruneInactive,
  namedLabelFor,
} from "@/lib/repeat-groups";

// Parse a date string in local time to avoid UTC timezone shift.
// "1982-03-30" → Date(1982, 2, 30) local, NOT UTC midnight.
const parseDateLocal = (v: any): Date | undefined => {
  if (v instanceof Date) return v;
  if (typeof v === "string" && v) {
    const parts = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (parts) {
      return new Date(parseInt(parts[1]), parseInt(parts[2]) - 1, parseInt(parts[3]));
    }
    const d = new Date(v);
    return isNaN(d.getTime()) ? undefined : d;
  }
  return undefined;
};

// Coerce a value to Date if it's an ISO string (DB resume case)
const toDate = (v: any): Date | undefined => parseDateLocal(v);

// Le type `checkbox` recouvre DEUX questions très différentes :
//   • la case unique de consentement (« Je déclare que… ») — aucune option, ou
//     une seule qui n'est que la formulation du consentement ;
//   • la question à choix multiples (« cochez tout ce qui s'applique ») — deux
//     options ou plus, une case par option, réponse = tableau de codes.
// Sans cette distinction, une question à choix multiples s'affichait comme une
// case de consentement : les options n'étaient jamais rendues et le client ne
// pouvait pas répondre. Le seuil est « au moins 2 options » parce que les
// questions de consentement importées portent parfois une option unique
// factice (par exemple ["Y"] ou [{code:"accepte"}]).
const isMultiChoiceCheckbox = (q: Question | undefined | null): boolean =>
  !!q && q.type === "checkbox" && (q.options?.length ?? 0) > 1;

// Réponse d'une question à choix multiples, toujours normalisée en tableau de
// codes (les anciennes réponses enregistrées en chaîne unique restent lisibles).
const asCheckboxCodes = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map((v) => String(v));
  if (typeof value === "string" && value !== "") return [value];
  return [];
};

// Multi-entry structured input component
interface MultiEntryInputProps {
  entries: Record<string, string>[];
  fields: MultiEntryFieldDef[];
  addLabel: string;
  onChange: (entries: Record<string, string>[]) => void;
  selectPlaceholder?: string;
  deleteConfirm?: string;
  entryLabel?: (n: number) => string;
  confirmDeleteAria?: (n: number) => string;
  removeAria?: (n: number) => string;
  language?: string;
  /** Colonnes écrites d'office dans la PREMIÈRE ligne (voir
   *  lib/prefill-premiere-ligne). Le client peut tout changer ensuite. */
  premiereLigne?: Record<string, string> | null;
}

function MultiEntryInput({
  entries,
  fields,
  addLabel,
  onChange,
  selectPlaceholder = "Sélectionnez...",
  deleteConfirm = "Supprimer ?",
  entryLabel = (n) => `Entrée ${n}`,
  confirmDeleteAria = (n) => `Confirmer la suppression de l'entrée ${n}`,
  removeAria = (n) => `Supprimer l'entrée ${n}`,
  language = "fr",
  premiereLigne = null,
}: MultiEntryInputProps) {
  const [confirmingDelete, setConfirmingDelete] = useState<number | null>(null);
  // Oui / Non dans la langue du client, pour les colonnes booléennes.
  const tCol = getUiTranslations(language);

  const addEntry = () => {
    // Chaque ligne a un identifiant stable : une liste répétée par personne s'en
    // sert pour retrouver ses écrans (voir lib/repeat-groups.ts), et il ne coûte
    // rien aux autres listes, dont les colonnes ignorent `_id`.
    const empty: Record<string, string> = { _id: newRowId() };
    fields.forEach((f) => {
      empty[f.key] = "";
    });
    // La première ligne d'un historique reprend ce que le client vient de
    // répondre (son adresse, son occupation) plutôt que de le faire retaper.
    const depart = entries.length === 0 && premiereLigne ? { ...empty, ...premiereLigne } : empty;
    onChange([...entries, depart]);
  };

  const updateEntry = (index: number, key: string, value: string) => {
    const updated = entries.map((e, i) =>
      i === index ? { ...e, [key]: value } : e,
    );
    onChange(updated);
  };

  const removeEntry = (index: number) => {
    onChange(entries.filter((_, i) => i !== index));
    setConfirmingDelete(null);
  };

  return (
    <div className="space-y-4">
      {entries.map((entry, idx) => (
        <div
          key={idx}
          className="p-4 bg-secondary/50 rounded-xl border border-border relative"
        >
          {confirmingDelete === idx ? (
            <button
              onClick={() => removeEntry(idx)}
              onBlur={() => setConfirmingDelete(null)}
              autoFocus
              className="absolute top-3 right-3 px-2 py-1 bg-destructive text-destructive-foreground text-xs font-medium rounded-lg transition-colors"
              type="button"
              aria-label={confirmDeleteAria(idx + 1)}
            >
              {deleteConfirm}
            </button>
          ) : (
            <button
              onClick={() => setConfirmingDelete(idx)}
              className="absolute top-3 right-3 p-1.5 hover:bg-destructive/10 rounded-lg transition-colors"
              type="button"
              aria-label={removeAria(idx + 1)}
            >
              <Trash2 className="w-4 h-4 text-destructive" />
            </button>
          )}
          <p className="text-xs font-semibold text-muted-foreground mb-3">
            {entryLabel(idx + 1)}
          </p>
          <div className="grid grid-cols-2 gap-3">
            {fields.map((field) => (
              <div
                key={field.key}
                className={field.colSpan === 2 ? "col-span-2" : ""}
              >
                <label className="text-xs text-muted-foreground mb-1 block">
                  {field.label}
                </label>
                {field.type === "select" && field.options ? (
                  (field.options.length > 20) ? (
                    <CountryCombobox
                      value={entry[field.key] || ""}
                      onValueChange={(val) => updateEntry(idx, field.key, val)}
                      placeholder={field.placeholder || selectPlaceholder}
                      language={language}
                    />
                  ) : (
                    <Select
                      value={(() => {
                        const raw = entry[field.key] || "";
                        const byName = field.options!.find((o) => o.name === raw);
                        if (byName) return byName.code;
                        return raw;
                      })()}
                      onValueChange={(code) => {
                        updateEntry(idx, field.key, code);
                      }}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue
                          placeholder={field.placeholder || selectPlaceholder}
                        />
                      </SelectTrigger>
                      <SelectContent className="max-h-64">
                        {field.options.map((o) => (
                          <SelectItem key={o.code} value={o.code}>
                            {getLocalizedOptionLabel(o, language)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )
                ) : field.type === "yes-no" || field.type === "boolean" ? (
                  // Une colonne Oui/Non doit se répondre en un geste : en champ
                  // libre le client écrit « oui », « yes », « x », et le
                  // remplissage du formulaire ne retrouve pas la case à cocher.
                  <Select
                    value={entry[field.key] || ""}
                    onValueChange={(val) => updateEntry(idx, field.key, val)}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder={field.placeholder || selectPlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="oui">{tCol.yes}</SelectItem>
                      <SelectItem value="non">{tCol.no}</SelectItem>
                    </SelectContent>
                  </Select>
                ) : field.type === "date" ? (
                  // Le même sélecteur que partout ailleurs dans le
                  // questionnaire, en version compacte. La valeur enregistrée
                  // reste une date ISO, construite sur l'heure locale pour ne
                  // pas reculer d'un jour selon le fuseau.
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full h-11 justify-start text-left font-normal text-sm",
                          !entry[field.key] && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                        {entry[field.key]
                          ? formatDateLocalized(entry[field.key] as any, language)
                          : tCol.selectDate}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-card" align="start">
                      <Calendar
                        mode="single"
                        selected={toDate(entry[field.key])}
                        onSelect={(date) => {
                          if (!date) return;
                          const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
                          updateEntry(idx, field.key, iso);
                        }}
                        initialFocus
                        captionLayout="dropdown-buttons"
                        fromYear={1920}
                        toYear={new Date().getFullYear() + 15}
                        className="p-3 pointer-events-auto"
                        locale={getDateFnsLocale(language)}
                      />
                    </PopoverContent>
                  </Popover>
                ) : field.type === "textarea" ? (
                  // Une explication (une période sans emploi, par exemple) tient
                  // rarement sur une ligne : une colonne « zone de texte »
                  // s'affichait pourtant comme un champ d'une seule ligne.
                  <Textarea
                    value={entry[field.key] || ""}
                    onChange={(e) => updateEntry(idx, field.key, e.target.value)}
                    placeholder={field.placeholder}
                    rows={3}
                  />
                ) : (
                  <Input
                    // Un montant ou un nombre de jours ouvre le clavier
                    // numérique plutôt que le clavier texte.
                    type={field.type === "number" ? "number" : "text"}
                    inputMode={field.type === "number" ? "decimal" : undefined}
                    value={entry[field.key] || ""}
                    onChange={(e) =>
                      updateEntry(idx, field.key, e.target.value)
                    }
                    placeholder={field.placeholder}
                    className="h-11"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <button
        onClick={addEntry}
        type="button"
        className="w-full p-3 border-2 border-dashed border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-colors flex items-center justify-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary"
      >
        <Plus className="w-4 h-4" />
        {addLabel}
      </button>
    </div>
  );
}

/** Une réponse en cours de saisie part d'elle-même ce délai après la dernière frappe. */
const DELAI_SAUVEGARDE_MS = 800;

export interface UnifiedWizardProps {
  onComplete: (data: FlowData) => void;
  onAnswerSaved?: (
    questionId: string,
    value: any,
    extras?: Record<string, any>,
    questionType?: string,
    questionLabel?: string,
    multiEntryFields?: MultiEntryFieldDef[],
    verification?: Record<string, { status: string; feedback?: string }>,
    // Une sauvegarde en cours de saisie : la valeur brute part tout de suite,
    // sans traduction (et l'ancienne traduction est retirée). Celle du
    // Continuer ou du Retour reste complète.
    intermediaire?: boolean,
  ) => void;
  onFeedbackSave?: (feedback: FeedbackData) => void;
  initialData?: FlowData;
  applicationId?: string | number | null;
  resumeFrom?: boolean;
  /** externalId of the question the client last had open (persisted). Resume
   *  restores this exact question; falls back to a heuristic if it's not
   *  currently visible. */
  resumeQuestionId?: string;
  /**
   * Dossier créé à partir d'une réponse à un formulaire public : les réponses
   * déjà données sont là, mais la personne n'a jamais ouvert CE questionnaire.
   * Reprendre « après la dernière réponse » la ferait atterrir au milieu et
   * sauter tout ce qui précède — on la pose sur la première question sans
   * réponse, les réponses reprises restant affichées quand elle y arrive.
   */
  startAtFirstUnanswered?: boolean;
  /** Fired on every step change with the current question's externalId so the
   *  position can be persisted. */
  onPositionChange?: (questionId: string) => void;
  skippedSections?: string[];
  /** What the top-left mark reads. A branded firm puts its own name there — the
   *  client is filling a form for their lawyer, not for us. "Formio" otherwise,
   *  and the "Powered by" line at the bottom keeps the attribution either way. */
  brandMark?: string;
  documentOnly?: boolean;
  dbQuestions?: Record<string, Question>;
  dbOrder?: string[];
  /**
   * Le cabinet a lui-même rangé les questions de ce dossier (éditeur du type ou
   * du client). Son ordre fait foi : un document qui pré-remplit des champs
   * remonte devant le premier d'entre eux, au lieu de faire descendre ces
   * champs derrière lui et de défaire le rangement.
   */
  ordreDuCabinet?: boolean;
  ocrCtx?: OcrContext;
  /** Per-client OCR config overrides (firm ocrFill wiring), merged over the
   *  global document configs. Keyed by document key. */
  ocrConfigOverrides?: Record<string, import("@/lib/document-ocr").DocumentOcrConfig>;
  saveStatus?: SaveStatus;
  uiT?: import("@/lib/ui-translations").UiTranslations;
  /** Current language code. When provided alongside onLanguageChange, a switcher appears in the wizard header. */
  preferredLanguage?: string;
  onLanguageChange?: (lang: SupportedLanguage) => void;
}

export function UnifiedWizard({ onComplete, onAnswerSaved, onFeedbackSave, initialData, applicationId, resumeFrom, resumeQuestionId, startAtFirstUnanswered = false, onPositionChange, skippedSections = [], brandMark = "Formio", documentOnly = false, dbQuestions, dbOrder, ordreDuCabinet = false, ocrCtx, ocrConfigOverrides, saveStatus = 'idle', uiT, preferredLanguage, onLanguageChange }: UnifiedWizardProps) {
  const t = uiT || getUiTranslations("fr");
  const allQuestions: Record<string, Question> = dbQuestions ?? (() => {
    const demandeData = getDemandeQuestions(typeof applicationId === 'number' ? applicationId : null);
    return { ...QUESTIONS, ...demandeData.questions };
  })();
  const allOrder: string[] = dbOrder ?? (() => {
    const demandeData = getDemandeQuestions(typeof applicationId === 'number' ? applicationId : null);
    return [...QUESTION_ORDER, ...demandeData.order];
  })();

  // Name questions are the OCR verification anchors: they appear BEFORE the
  // passport (see getOrderedQuestions). No field is hidden after a scan anymore,
  // so names — like every extracted field — stay reachable via back-nav.
  const NAME_IDS = new Set(['firstName', 'lastName', 'fullName', 'd2FullName', 'd3FullName', 'd4GuestFullName']);

  const getOrderedQuestions = (): Question[] => {
    const questions = allOrder.map(id => allQuestions[id]).filter(Boolean);

    // Ordre posé par le cabinet : c'est le DOCUMENT qui se déplace. Il remonte
    // juste devant le premier champ qu'il pré-remplit, et l'ordre des questions
    // reste exactement celui du cabinet. Sans cela, la règle plus bas faisait
    // redescendre derrière le passeport la nationalité ou la ville de naissance
    // que le cabinet venait de placer en tête : le client ne voyait jamais
    // l'ordre choisi pour lui. Fait AVANT la règle des noms, pour qu'un nom
    // reste devant le passeport même quand le passeport remonte.
    if (ordreDuCabinet) {
      for (const [docId, config] of Object.entries(documentOcrConfigs)) {
        const fillIds = new Set(
          Object.values(config.fieldMapping)
            .map((m) => m.wizardFieldId)
            .filter((fid) => !NAME_IDS.has(fid)),
        );
        if (fillIds.size === 0) continue;
        const docIdx = questions.findIndex(q => q.id === docId);
        if (docIdx === -1) continue;
        const premier = questions.findIndex(q => fillIds.has(q.id));
        if (premier === -1 || premier > docIdx) continue;
        // Trois bornes : le document ne passe jamais devant un nom (la lecture
        // du passeport compare le prénom ET le nom), ni devant la question qui
        // le fait apparaître (une pièce sous condition remontée au-dessus de sa
        // condition ne ressortirait qu'à la relecture), ni en tête du
        // questionnaire. Un champ rangé plus haut que ces bornes reste où le
        // cabinet l'a mis : il se tape à la main, l'ordre du cabinet passe avant.
        const doc = questions[docIdx];
        const apres = (i: number) => (i === -1 ? 0 : i + 1);
        const dernierNom = questions.reduce((m, q, i) => (NAME_IDS.has(q.id) && i < docIdx ? i : m), -1);
        const conds = doc.dependsOn ? (Array.isArray(doc.dependsOn) ? doc.dependsOn : [doc.dependsOn]) : [];
        const derniereCondition = conds.reduce((m, c) => {
          const i = questions.findIndex(q => q.id === c.questionId);
          return i < docIdx ? Math.max(m, i) : m;
        }, -1);
        const cible = Math.max(premier, apres(dernierNom), apres(derniereCondition), 1);
        if (cible >= docIdx) continue;
        questions.splice(docIdx, 1);
        questions.splice(cible, 0, doc);
      }
    }

    // Name questions must always appear before any passport/document question
    // so the applicant's name is available for OCR verification. Enforce this
    // regardless of DB or static order.
    const PASSPORT_IDS = new Set(['passportDocument']);

    const nameIdx = questions.findIndex(q => NAME_IDS.has(q.id));
    const passportIdx = questions.findIndex(q => PASSPORT_IDS.has(q.id));

    if (nameIdx !== -1 && passportIdx !== -1 && nameIdx > passportIdx) {
      const nameQ = questions.splice(nameIdx, 1)[0];
      questions.splice(passportIdx, 0, nameQ);
    }

    // A field a document OCR-fills must never appear BEFORE that document —
    // otherwise the applicant types it manually and the scan is pointless
    // ("ça sert à rien"). Relocate any such field to just after its document,
    // preserving relative order. Names are the deliberate exception (handled
    // above): they stay before as the OCR verification anchor.
    // (Avec un ordre du cabinet, le document est déjà remonté : rien ne bouge ici.)
    for (const [docId, config] of Object.entries(documentOcrConfigs)) {
      const fillIds = new Set(
        Object.values(config.fieldMapping)
          .map((m: any) => m.wizardFieldId)
          .filter((fid: string) => !NAME_IDS.has(fid)),
      );
      if (fillIds.size === 0 || ordreDuCabinet) continue;
      let docIdx = questions.findIndex(q => q.id === docId);
      if (docIdx === -1) continue;
      const moved: Question[] = [];
      for (let i = docIdx - 1; i >= 0; i--) {
        if (fillIds.has(questions[i].id)) {
          moved.unshift(questions.splice(i, 1)[0]);
          docIdx--;
        }
      }
      if (moved.length) questions.splice(docIdx + 1, 0, ...moved);
    }

    return questions;
  };

  const ALWAYS_ASK_IDS = new Set(['d2FullName', 'd3FullName', 'd4GuestFullName']);

  // Listes répétées par personne : leurs écrans sont fabriqués à partir des
  // lignes déjà saisies (lib/repeat-groups.ts).
  const repeatGroupIds = Object.values(allQuestions)
    .filter((q) => isRepeatGroup(q))
    .map((q) => q.id);
  // Les lignes enregistrées avant la répétition reçoivent un identifiant.
  const [seededData] = useState<FlowData | undefined>(() =>
    initialData ? ensureRowIds(initialData, repeatGroupIds) : initialData,
  );

  // `ocrFilledFields` is intentionally IGNORED now. Hiding OCR-fillable fields
  // from the flow is what broke the wizard: fields vanished blank when a scan was
  // partial (data loss + a ~20-question jump), and hidden steps/documents could
  // not be found on resume. OCR now only PREFILLS values (shown with a badge, and
  // confirmable); nothing is removed from the list. Only real logic hides a
  // question: documentOnly mode, firm-skipped sections, and dependsOn. The param
  // is kept so callers don't all need editing.
  const getVisibleQuestions = (answers: Record<string, any>, _ocrFilledFields?: Set<string>): Question[] => {
    // Une liste répétée est suivie des écrans de chacune de ses personnes. Ils
    // n'existent que si la liste elle-même est visible, et passent ensuite par
    // le même filtre que les autres questions (leurs conditions visent les
    // autres cases de la même personne).
    // En mode « documents seulement », la liste elle-même est masquée mais les
    // documents de ses personnes (leur passeport) restent demandés.
    const withPersons: Question[] = [];
    for (const q of getOrderedQuestions()) {
      const shown = isShown(q, answers);
      if (shown) withPersons.push(q);
      if (isRepeatGroup(q) && (shown || documentOnly)) {
        // « Personne 1 » ne convient qu'à une liste de personnes ; une liste
        // d'occupations ou de diplômes retombe sur « Entrée 1 ».
        const secours = isPersonGroup(q) ? t.repeatPerson : t.entry;
        withPersons.push(...expandGroup(q, answers, secours).filter((v) => isShown(v, answers)));
      }
    }
    // Une question qui nomme les personnes déjà entrées dans une liste.
    return withPersons.map((q) =>
      q.namedLabel ? { ...q, label: namedLabelFor(q, answers, preferredLanguage) } : q,
    );
  };

  const isShown = (q: Question, answers: Record<string, any>): boolean => {
      // Name questions are always shown regardless of documentOnly or skipped sections
      if (ALWAYS_ASK_IDS.has(q.id)) return true;
      if (documentOnly && q.type !== 'document') return false;
      const sKey = q.sectionKey || q.section;
      if (sKey && skippedSections.includes(sKey)) return false;
      if (!q.dependsOn) return true;
      // dependsOn is either a single { questionId, value } (gate on one parent)
      // or an array of them (OR across parents — show if ANY matches). Lets a
      // "details" box depend on e.g. Q1A OR Q1B. `value` may itself be an array
      // (multi-value match for one parent).
      const conds = Array.isArray(q.dependsOn) ? q.dependsOn : [q.dependsOn];
      return conds.some((c) => {
        const actual = readAnswer(answers, c.questionId);
        const expected = Array.isArray(c.value) ? c.value : [c.value];
        // Une réponse à choix multiples (tableau de codes) correspond dès
        // qu'un de ses éléments est attendu. Même règle côté serveur
        // (publicFormLogic.conditionMatches).
        if (Array.isArray(actual)) return actual.some((a) => expected.includes(a));
        return expected.includes(actual);
      });
  };

  // OCR doc configs pulled from Convex `documents` table when the query
  // resolves; falls back to the local `DOCUMENT_OCR_CONFIGS` while loading
  // or on error so the wizard never has a window where doc upload is
  // broken. Defined here (before any useState that calls
  // `getOcrFieldsToSkip` via its initializer) to avoid a TDZ ReferenceError.
  const globalOcrConfigs = useConvexDocumentOcrConfigs();
  // Per-client overrides (firm ocrFill wiring) win per document key — their
  // config already contains the full fills list (canonical + injected).
  const documentOcrConfigs = ocrConfigOverrides
    ? { ...globalOcrConfigs, ...ocrConfigOverrides }
    : globalOcrConfigs;

  // Build the set of OCR field IDs to skip based on which documents were scanned.
  // A document being provided lets us skip the upload step, but we may ONLY skip
  // an extracted field if the OCR actually filled it. Skipping every mapped field
  // the moment the doc has a value assumed extraction always succeeds — when it
  // failed or was partial, those fields vanished BLANK (the applicant was jumped
  // ~20 unanswered questions ahead, silently dropping data). Skip a field only
  // when it has a real value; unfilled fields stay visible for manual entry.
  const getOcrFieldsToSkip = (answers: Record<string, any>): Set<string> => {
    // A provided document hides only its OWN upload step. Its extracted fields
    // are now SHOWN prefilled (with a "rempli par le scan" badge, editable) so
    // the applicant reviews/corrects them — essential for a legal file, and it
    // keeps every field reachable via back-nav. Hiding them (the old behavior)
    // silently dropped data when OCR was partial and made it impossible to
    // navigate back to the name questions after uploading the passport.
    const skipSet = new Set<string>();
    for (const docQuestionId of Object.keys(documentOcrConfigs)) {
      const val = answers[docQuestionId];
      if (val != null && val !== '' && val !== '__no_document__' && val !== '__skipped__') {
        if (allQuestions[docQuestionId]) skipSet.add(docQuestionId);
      }
    }
    return skipSet;
  };

  // OCR-mapped wizard fields that currently hold a value, for documents that were
  // provided — i.e. the fields a scan prefilled. Used to seed the "rempli par le
  // scan" badge on resume (during a live session applyOcrFields tracks them).
  const getOcrPrefilledFields = (answers: Record<string, any>): Set<string> => {
    const s = new Set<string>();
    for (const [docQuestionId, config] of Object.entries(documentOcrConfigs)) {
      const dv = answers[docQuestionId];
      if (dv == null || dv === '' || dv === '__no_document__' || dv === '__skipped__') continue;
      for (const mapping of Object.values(config.fieldMapping)) {
        const fid = mapping.wizardFieldId;
        const fval = answers[fid];
        if (allQuestions[fid] && fval != null && fval !== '') s.add(fid);
      }
    }
    return s;
  };

  // When resuming, find the position AFTER the last answered question.
  // Using "last answered + 1" (rather than "first unanswered") means the user is placed
  // right after their furthest progress, even if they skipped questions along the way
  // (the skip button advances without saving an answer, so those questions look unanswered).
  const getResumeIndex = (): number => {
    if (!resumeFrom || !initialData) {
      return 0;
    }
    const init = seededData;
    const resumeSkipSet = getOcrFieldsToSkip(init);
    const visible = getVisibleQuestions(init, resumeSkipSet);

    // Preferred: restore the EXACT question the client last had open. It's saved
    // by externalId, so it survives the list being rebuilt (reordering,
    // conditional visibility, OCR prefill). This is the deterministic path.
    if (resumeQuestionId) {
      const savedIdx = visible.findIndex((q) => q.id === resumeQuestionId);
      if (savedIdx !== -1) return savedIdx;
    }

    if (startAtFirstUnanswered) {
      const idx = visible.findIndex((q) => {
        const val = readAnswer(init, q.id);
        return val === undefined || val === null || val === '';
      });
      return idx === -1 ? 0 : idx;
    }

    // Fallback only (older submissions with no saved position, or the saved
    // question is no longer visible): resume after the furthest answered
    // question. Fragile — see the note above — but better than starting at 0.
    let lastAnsweredIdx = -1;
    visible.forEach((q, idx) => {
      const val = readAnswer(init, q.id);
      if (val !== undefined && val !== null && val !== '') {
        lastAnsweredIdx = idx;
      }
    });

    // Resume right after the last answered question (or start from 0 if nothing saved)
    const index = lastAnsweredIdx === -1 ? 0 : Math.min(lastAnsweredIdx + 1, visible.length - 1);
    return index;
  };

  const [currentStepIndex, setCurrentStepIndex] = useState(getResumeIndex);
  const [data, setData] = useState<FlowData>(() => seededData || {});
  // Dernier état connu des réponses, à jour dès l'écriture. Une personne d'une
  // liste répétée s'enregistre en renvoyant TOUTE la liste : deux écritures
  // rapprochées (la raison d'un « passer » puis le « passer » lui-même) doivent
  // partir chacune de la précédente, pas d'un rendu en retard.
  const dataRef = useRef<FlowData>(data);
  dataRef.current = data;
  // Ce que la base connaît de chaque réponse, telle que cet onglet l'a chargée
  // ou envoyée. Continuer n'envoie une réponse que si elle diffère de ça : le
  // 28 oct. 2026, le testeur a vu sur staging un onglet resté sur une vieille
  // valeur l'imposer de nouveau en cliquant Continuer sans y toucher, et
  // effacer la correction faite entre-temps dans un autre onglet. Un onglet
  // n'envoie que ce que le client y a changé, comme la console (PR #19).
  const connuDeLaBase = useRef<Record<string, string>>({});
  useEffect(() => {
    const connu: Record<string, string> = {};
    for (const [k, v] of Object.entries(dataRef.current)) connu[k] = JSON.stringify(v ?? null);
    connuDeLaBase.current = connu;
  }, []);
  const memeValeurQueLaBase = (id: string, valeur: any) =>
    connuDeLaBase.current[id] !== undefined && connuDeLaBase.current[id] === JSON.stringify(valeur ?? null);
  const noterEnvoye = (id: string, valeur: any) => {
    connuDeLaBase.current[id] = JSON.stringify(valeur ?? null);
  };

  const [prefilledFields, setPrefilledFields] = useState<Set<string>>(() => {
    if (!initialData) return new Set();
    if (resumeFrom) {
      // On resume, badge the fields a scan actually prefilled (that still hold a
      // value), not the raw skip set.
      return getOcrPrefilledFields(initialData);
    }
    // Fresh session with initialData: all fields came from a document scan.
    const keys = Object.keys(initialData).filter(key => allQuestions[key]);
    return new Set(keys);
  });

  // Fields filled by OCR scans that should be skipped in the flow
  const [ocrSkipFields, setOcrSkipFields] = useState<Set<string>>(() => {
    if (!initialData) return new Set();
    if (resumeFrom) {
      // When resuming, skip all fields from scanned documents (even if OCR didn't extract them)
      return getOcrFieldsToSkip(initialData);
    }
    // Fresh session: skip all pre-filled fields from passport scan
    const keys = Object.keys(initialData).filter(key => allQuestions[key]);
    return new Set(keys);
  });

  // Fields the user typed manually — OCR must never override these.
  // Initialised with any initialData keys that were NOT set by OCR (resume case).
  const manuallyFilledFields = useRef<Set<string>>(
    (() => {
      if (!initialData) return new Set<string>();
      const ocrFields = resumeFrom
        ? getOcrFieldsToSkip(initialData)
        : new Set(Object.keys(initialData).filter((key) => allQuestions[key]));
      const manual = new Set<string>();
      for (const key of Object.keys(initialData)) {
        if (!ocrFields.has(key)) manual.add(key);
      }
      return manual;
    })()
  );
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showManualAddress, setShowManualAddress] = useState(false);
  const [autocompleteAddress, setAutocompleteAddress] = useState<import("@/hooks/useGooglePlacesAutocomplete").ParsedAddress | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isWhyOpen, setIsWhyOpen] = useState(false);
  const [showReview, setShowReview] = useState(false);

  // Skip-reason capture: when a client skips a question or declines a document,
  // prompt them (free text) for WHY, so the consultant can tell an intentional
  // blank ("N/A — never married") from an oversight. The reason is persisted to
  // a parallel `${id}__skipReason` answer key. Opening the dialog stashes the
  // original skip action as `proceed`, run once the reason is captured.
  const [skipDialog, setSkipDialog] = useState<
    null | { qid: string; isDoc: boolean; proceed: () => void }
  >(null);
  const [skipReason, setSkipReason] = useState("");
  const confirmSkip = () => {
    const d = skipDialog;
    if (!d) return;
    const reason = skipReason.trim();
    if (!reason) return; // reason is required to skip
    // Pour une personne d'une liste répétée, la raison se range dans sa ligne
    // (`colonne__skipReason`), à côté de la case qu'elle explique.
    const key = `${d.qid}__skipReason`;
    setData((prev) => writeAnswer(prev, key, reason));
    persistAnswer(key, reason);
    setSkipDialog(null);
    setSkipReason("");
    d.proceed();
  };
  const [showFeedback, setShowFeedback] = useState(false);
  const [confirmingFileDelete, setConfirmingFileDelete] = useState<number | null>(null);
  const [reviewModeQuestions, setReviewModeQuestions] = useState<Question[] | null>(null);
  const editingFromReview = useRef(false);
  // For document uploads
  // pendingFile holds the selected file BEFORE the user proceeds (not committed to data yet).
  // This prevents a rejected document from appearing as "accepted" if OCR state ever resets.
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Tracks whether the current question has unsaved edits (for beforeunload).
  const isDirty = useRef(false);
  // La précision d'un oui/non ne part qu'au Continuer (dans `extras`). Son
  // drapeau est à part : la minuterie de la question principale remet
  // `isDirty` à faux quand elle envoie, et ne doit pas faire taire
  // l'avertissement pour elle.
  // On retient la QUESTION dont la case annexe attend : seul le Continuer de
  // cette question-là lève le drapeau (un Retour puis un Continuer ailleurs
  // ne l'efface pas).
  const annexeNonEnregistree = useRef<string | null>(null);
  // Prevents double-firing when clicking Continuer/Retour rapidly.
  // The lock is acquired immediately on click and released in useLayoutEffect
  // (after the component re-renders), so the next click always sees fresh state.
  const isNavigating = useRef(false);
  // Les petites minuteries d'interface (le voyant « enregistré », le passage à
  // la question suivante) ne doivent plus rien toucher une fois le composant
  // démonté. Dans le navigateur ça ne change rien ; sous test, une minuterie
  // qui survivait à la fin du test faisait planter la suite (« window is not
  // defined », trois fois en CI le 28 oct. 2026).
  const monte = useRef(true);
  useEffect(() => {
    monte.current = true;
    return () => { monte.current = false; };
  }, []);
  const plusTard = useCallback((fn: () => void, ms: number) => {
    setTimeout(() => { if (monte.current) fn(); }, ms);
  }, []);
  const releaseLockAfterRender = useRef(false);
  useLayoutEffect(() => {
    if (releaseLockAfterRender.current) {
      isNavigating.current = false;
      releaseLockAfterRender.current = false;
    }
  });
  // Auto-advance (Oui/Non, case à cocher) is a delayed setTimeout. If it isn't
  // cancelled when the user navigates in the meantime (e.g. taps Retour before
  // the delay elapses), it fires later and jumps FORWARD from the new step —
  // exactly the "je fais Retour et ça me met Suivant" bug. Track the pending
  // timer so navigation can cancel it.
  const autoAdvanceTimer = useRef<number | null>(null);
  const clearAutoAdvance = () => {
    if (autoAdvanceTimer.current != null) {
      window.clearTimeout(autoAdvanceTimer.current);
      autoAdvanceTimer.current = null;
    }
  };
  useEffect(() => () => clearAutoAdvance(), []);

  // Warn before closing tab when there are unsaved edits
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // S'il reste quelque chose de non enregistré, on tente l'envoi, mais on
      // retient quand même le client : une page qui se ferme n'attend pas la
      // réponse du serveur.
      if (isDirty.current || annexeNonEnregistree.current !== null) {
        enregistrerAvantDeFermer.current();
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // Document OCR
  const {
    status: ocrStatus,
    progress: ocrProgress,
    result: ocrResult,
    scan: ocrScan,
    reset: ocrReset,
    attemptCount: ocrAttemptCount,
    maxAttempts: ocrMaxAttempts,
    hasRetriesLeft: ocrHasRetriesLeft,
  } = useDocumentOcr();

  // Editable OCR fields state
  const [editableOcrFields, setEditableOcrFields] = useState<Record<string, any>>({});

  const applyOcrFields = useCallback(() => {
    if (!ocrResult) return;
    const fields = ocrResult.extractedFields;

    // Never override a field the user typed manually.
    const toApply: Record<string, any> = {};
    for (const [key, value] of Object.entries(fields)) {
      if (!manuallyFilledFields.current.has(key)) {
        toApply[key] = value;
      }
    }

    setData((prev) => mergeAnswers(prev, toApply));
    setEditableOcrFields(fields); // keep full set for display purposes
    setPrefilledFields((prev) => {
      const next = new Set(prev);
      Object.keys(toApply).forEach((key) => next.add(key));
      return next;
    });
  }, [ocrResult]);

  // Auto-apply OCR results when they come in
  useEffect(() => {
    if (ocrStatus === "success" && ocrResult) {
      applyOcrFields();
    }
  }, [ocrStatus, ocrResult, applyOcrFields]);

  // Questions the client's documents will OCR-fill: skip them in the active flow
  // and the counter so the client only steps through what they actually type.
  // `ocrSkipFields` covers fields already filled; here we ALSO skip fields a
  // (not-yet-declined) document is expected to fill, so the count is low from the
  // start. A declined document (`__no_document__`/`__skipped__`) re-includes its
  // fields, and the review step recomputes from the full set, so anything OCR
  // misses still surfaces before submit.
  const ocrFlowSkip = (() => {
    const s = new Set(ocrSkipFields);
    // Position of each question in the flow, so we only optimistically hide a
    // document's fill-fields that come AFTER the document. Hiding fields BEFORE
    // the doc made them "pop up" earlier in the flow (behind the cursor) the
    // moment the doc was declined — a disorienting count/order jump. Fields
    // after the doc re-appear ahead of the cursor on decline, in order.
    const ordered = getOrderedQuestions();
    const posOf = new Map(ordered.map((q, i) => [q.id, i]));
    for (const [docQuestionId, config] of Object.entries(documentOcrConfigs)) {
      const val = data[docQuestionId];
      if (val === '__no_document__' || val === '__skipped__') continue;
      const docPos = posOf.get(docQuestionId);
      if (docPos == null) continue;
      for (const mapping of Object.values(config.fieldMapping)) {
        const fieldPos = posOf.get(mapping.wizardFieldId);
        if (allQuestions[mapping.wizardFieldId] && fieldPos != null && fieldPos > docPos) {
          s.add(mapping.wizardFieldId);
        }
      }
    }
    return s;
  })();
  const visibleQuestions = getVisibleQuestions(data, ocrFlowSkip);
  // In review mode, only show the unanswered questions
  const activeQuestions = reviewModeQuestions ?? visibleQuestions;
  const currentQuestion = activeQuestions[currentStepIndex];
  const totalSteps = activeQuestions.length;

  // Safety net: if the active question list shrinks (e.g. flipping a controller
  // answer hides a trailing branch), currentStepIndex can point past the end and
  // currentQuestion becomes undefined, rendering a blank screen. Clamp it back
  // into range so the user always lands on a valid step.
  useEffect(() => {
    if (!showReview && totalSteps > 0 && currentStepIndex > totalSteps - 1) {
      setCurrentStepIndex(totalSteps - 1);
    }
  }, [showReview, totalSteps, currentStepIndex]);

  // Persist the current position (by externalId) so a reload resumes exactly
  // here. Only in the normal flow — review mode uses a transient question set.
  useEffect(() => {
    if (onPositionChange && currentQuestion?.id && !showReview && !reviewModeQuestions) {
      onPositionChange(currentQuestion.id);
    }
  }, [currentQuestion?.id, showReview, reviewModeQuestions, onPositionChange]);

  // Current section
  const currentSection = currentQuestion?.section;
  const prevQuestion =
    currentStepIndex > 0 ? activeQuestions[currentStepIndex - 1] : null;
  const isNewSection =
    currentSection &&
    (!prevQuestion || prevQuestion.section !== currentSection);

  // Compute section-level progress
  const sectionQuestions = activeQuestions.filter(q => q.section === currentSection);
  const stepInSection = sectionQuestions.findIndex(q => q === currentQuestion);
  const totalInSection = sectionQuestions.length;

  // Auto-focus input
  useEffect(() => {
    if (
      currentQuestion?.type === "text" ||
      currentQuestion?.type === "number"
    ) {
      setTimeout(() => textInputRef.current?.focus(), 100);
    }
    if (currentQuestion?.type === "textarea") {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
    // Pre-check confirmation when returning to a question that was already answered
    // (e.g. going back, or review mode). If the user changes the value, it will be unchecked.
    if (currentQuestion?.requiresConfirmation && readAnswer(data, currentQuestion.id)) {
      setConfirmed(true);
    } else {
      setConfirmed(false);
    }
    setError(null);
    setFieldErrors({});
    setShowManualAddress(false);
    setAutocompleteAddress(null);
    setIsWhyOpen(false);
    setPreviewUrl(null);
    setEditableOcrFields({});
    setPendingFile(null);
    ocrReset();
  }, [currentStepIndex, currentQuestion?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const getCurrentValue = () => {
    if (!currentQuestion) return undefined;
    const val = readAnswer(data, currentQuestion.id);
    if (val === "__skipped__" || val === "__no_document__") return undefined;
    return val;
  };

  // `sansSauvegarde` : la valeur change à l'écran sans programmer d'envoi
  // (un complément posé par la validation, un passage de question : ceux-là
  // ont leur propre envoi, ou aucun tant que Continuer n'a pas réussi). Une
  // sauvegarde déjà en attente, elle, reste : c'est la saisie du client.
  const updateValue = (value: any, { sansSauvegarde = false } = {}) => {
    if (!currentQuestion) return;
    const id = currentQuestion.id;
    const flat = isFlatVirtual(currentQuestion);
    setData((prev) => (flat ? { ...prev, [id]: value } : writeAnswer(prev, id, value)));
    if (currentQuestion.requiresConfirmation) setConfirmed(false);
    prefilledFields.delete(currentQuestion.id);
    manuallyFilledFields.current.add(currentQuestion.id);
    isDirty.current = true;
    setError(null);
    if (!sansSauvegarde) programmerLaSauvegarde(currentQuestion, value);
  };

  // Une réponse part dès qu'elle est saisie, pas seulement au clic sur
  // Continuer. Le 28 oct. 2026, le testeur a constaté sur staging qu'une
  // réponse tapée puis abandonnée (rafraîchissement, bouton retour) n'existait
  // nulle part : la règle « sauvegardée à chaque question » ne tenait qu'au
  // clic. Désormais la dernière valeur saisie part d'elle-même peu après la
  // dernière frappe, et tout de suite au Retour. Les pièces ne passent pas
  // par ici : leur fichier part avec son téléversement, au Continuer. Les
  // précision d'un oui/non non plus : elle part au Continuer, comme avant.
  const sauvegardeEnAttente = useRef<{ id: string; valeur: unknown; type: string; label: string; fields?: MultiEntryFieldDef[] } | null>(null);
  const minuterieSauvegarde = useRef<number | null>(null);
  // Les questions dont la dernière valeur partie est BRUTE (sans traduction) :
  // au Retour, elles repartent complètes, même si la minuterie a déjà fait son
  // travail. Sinon un client anglophone qui tape, attend une seconde et clique
  // Retour laisserait sa réponse sans traduction jusqu'à un Continuer.
  const envoyeesBrutes = useRef<Set<string>>(new Set());
  const annulerLaSauvegardeEnAttente = () => {
    if (minuterieSauvegarde.current != null) {
      window.clearTimeout(minuterieSauvegarde.current);
      minuterieSauvegarde.current = null;
    }
    sauvegardeEnAttente.current = null;
  };
  const enregistrerCeQuiAttend = ({ complete = false } = {}) => {
    const p = sauvegardeEnAttente.current;
    annulerLaSauvegardeEnAttente();
    if (!p) return;
    // Intermédiaire (après la frappe) : la valeur brute part tout de suite,
    // sans passer par la traduction ; rien de partiel ne part vers l'IA, et
    // la page met les envois d'une même question à la file, dans l'ordre.
    // Complète (au Retour) : comme un Continuer, traduction comprise.
    persistAnswer(p.id, p.valeur, undefined, p.type, p.label, p.fields, undefined, !complete);
    if (complete) envoyeesBrutes.current.delete(p.id); else envoyeesBrutes.current.add(p.id);
    // L'envoi est parti : plus rien à avertir à la fermeture. Hors ligne, le
    // client Convex le garde en file et le rejoue (scénario 3), comme pour
    // un Continuer.
    isDirty.current = false;
  };
  // Vide : rien, ou un texte fait d'espaces seuls (le testeur a vu cinq espaces
  // partir et écraser la vraie réponse, 30 oct. 2026).
  const estVide = (valeur: unknown) =>
    valeur === null || valeur === undefined || (typeof valeur === "string" && valeur.trim() === "") ||
    (Array.isArray(valeur) && valeur.length === 0);
  const programmerLaSauvegarde = (q: Question, valeur: unknown) => {
    if (q.type === "document" || q.type === "message") return;
    // Un champ vidé n'efface rien de lui-même : c'est souvent le geste qui
    // précède une nouvelle saisie. Continuer garde son mot à dire.
    if (estVide(valeur)) {
      annulerLaSauvegardeEnAttente();
      return;
    }
    // Revenu à ce que la base connaît déjà (« Lavall » puis « Laval ») : rien
    // à envoyer, et surtout pas une brute qui retirerait la traduction. Sauf
    // si une brute est déjà partie entre-temps (« Lavall » en base) : il faut
    // alors renvoyer « Laval », sinon l'écran et la base divergent.
    if (memeValeurQueLaBase(q.id, valeur) && !envoyeesBrutes.current.has(q.id)) {
      annulerLaSauvegardeEnAttente();
      return;
    }
    sauvegardeEnAttente.current = { id: q.id, valeur, type: q.type, label: q.label, fields: q.multiEntryFields };
    if (minuterieSauvegarde.current != null) window.clearTimeout(minuterieSauvegarde.current);
    minuterieSauvegarde.current = window.setTimeout(() => {
      minuterieSauvegarde.current = null;
      enregistrerCeQuiAttend();
    }, DELAI_SAUVEGARDE_MS);
  };
  // La version la plus récente, pour le démontage de l'écran (le nettoyage
  // d'un effet ne voit que la première). Au démontage, l'envoi est tenté,
  // sans garantie : ce qui est garanti, c'est l'envoi peu après la frappe.
  const enregistrerAvantDeFermer = useRef(enregistrerCeQuiAttend);
  enregistrerAvantDeFermer.current = enregistrerCeQuiAttend;
  useEffect(() => () => enregistrerAvantDeFermer.current(), []);

  // Enregistre une réponse. Pour l'écran d'une personne, c'est la liste entière
  // qui part, avec la case de cette personne changée : c'est la forme que la
  // console, la traduction et le remplissage du PDF lisent déjà.
  const persistAnswer = (
    id: string,
    value: any,
    extras?: Record<string, any>,
    type?: string,
    label?: string,
    multiEntryFields?: MultiEntryFieldDef[],
    verification?: Record<string, { status: string; feedback?: string }>,
    intermediaire = false,
  ) => {
    if (!onAnswerSaved) return;
    if (!intermediaire) envoyeesBrutes.current.delete(id);
    // Une sauvegarde intermédiaire part brute, sans traduction : la base ne
    // « connaît » la réponse qu'une fois le Continuer passé, pour que
    // celui-ci l'envoie entière.
    if (!intermediaire) {
      noterEnvoye(id, value);
      for (const [cle, val] of Object.entries(extras ?? {})) if (!(val instanceof File)) noterEnvoye(cle, val);
    }
    const v = parseVirtualId(id);
    if (!v) {
      const listQ = allQuestions[id];
      const sent = isRepeatGroup(listQ) && Array.isArray(value) ? pruneInactive(listQ, value) : value;
      onAnswerSaved(id, sent, extras, type, label, multiEntryFields, verification, intermediaire);
      return;
    }
    const group = allQuestions[v.groupId];
    // Le document d'une personne : le fichier part seul, à plat, par le
    // téléversement habituel ; ce que sa lecture a rempli rejoint ensuite la
    // ligne de la personne, avec la liste entière.
    // La liste part toujours entière, sans les cases qui ne s'appliquent plus à
    // leur personne (un « non » revenu sur « passeport valide » retire son
    // numéro du dossier enregistré ; l'écran le retrouve si le client revient
    // au « oui »). Elle part aussi après un document : c'est ce qui fige
    // l'identifiant des lignes enregistrées avant la répétition, auquel le
    // fichier est rattaché.
    const sendGroup = (snapshot: FlowData) =>
      onAnswerSaved(
        v.groupId,
        group ? pruneInactive(group, snapshot[v.groupId]) : snapshot[v.groupId],
        undefined,
        "multi-entry",
        group?.label,
        group?.multiEntryFields,
        undefined,
        intermediaire,
      );
    if (type === "document") {
      onAnswerSaved(id, value, undefined, type, label, undefined, verification);
      dataRef.current = { ...dataRef.current, [id]: value };
      const lus = Object.fromEntries(Object.entries(extras ?? {}).filter(([k]) => parseVirtualId(k)));
      const next = mergeAnswers(dataRef.current, lus);
      dataRef.current = next;
      sendGroup(next);
      return;
    }
    const next = writeAnswer(dataRef.current, id, value);
    dataRef.current = next;
    sendGroup(next);
  };

  // La lecture automatique d'un écran : celle de sa question, ou, pour le
  // document d'une personne, celle du document d'origine redirigée vers les
  // cases de cette personne.
  const ocrConfigFor = (q: Question | undefined) => {
    if (!q) return undefined;
    if (q.repeatOf?.ocr) {
      return personOcrConfig(q, documentOcrConfigs[q.repeatOf.ocr.document], allQuestions[q.repeatOf.groupId]);
    }
    return documentOcrConfigs[q.id];
  };
  // Le nom attendu sur un document : celui de la personne concernée.
  const expectedNameFor = (q: Question | undefined): string | undefined => {
    if (q?.repeatOf) return q.repeatOf.personName;
    return [data.firstName, data.lastName].filter(Boolean).join(" ") || (data.fullName as string | undefined) || undefined;
  };

  // Clear a single inline field error when the user starts editing that field
  const clearFieldError = (field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // Validate a single sub-field on blur and show inline error if empty
  const validateFieldOnBlur = (field: string, value: string | undefined, label: string) => {
    if (!currentQuestion?.isRequired) return;
    if (!value?.trim()) {
      setFieldErrors((prev) => ({ ...prev, [field]: t.fillField(label) }));
    } else {
      clearFieldError(field);
    }
  };

  const validateAndProceed = (overrideValue?: any) => {
    if (!currentQuestion) return;
    if (isNavigating.current) return;
    clearAutoAdvance(); // a manual advance supersedes any pending auto-advance



    // Checkbox type - must be checked
    if (currentQuestion.type === "checkbox") {
      if (isMultiChoiceCheckbox(currentQuestion)) {
        // Choix multiples : au moins une option cochée
        if (
          currentQuestion.isRequired &&
          asCheckboxCodes(overrideValue ?? getCurrentValue()).length === 0
        ) {
          setError(t.selectOption);
          return;
        }
      } else if (currentQuestion.isRequired && !(overrideValue ?? getCurrentValue())) {
        setError(t.acceptRequired);
        return;
      }
    } else if (currentQuestion.type === "address") {
      const addr = (getCurrentValue() as any) || {};
      if (currentQuestion.isRequired) {
        const errs: Record<string, string> = {};
        if (!addr.street?.trim()) errs.street = t.enterStreet;
        if (!addr.city?.trim()) errs.city = t.enterCity;
        if (!addr.province?.trim()) errs.province = t.enterProvince;
        if (!addr.country) errs.country = t.selectCountry;
        if (!addr.postalCode?.trim()) errs.postalCode = t.enterPostalCode;
        if (Object.keys(errs).length > 0) {
          setFieldErrors(errs);
          setError(t.fillRequired);
          return;
        }
      }
    } else if (currentQuestion.type === "phone") {
      const phone = (getCurrentValue() as any) || {};
      // Default to Canada if user never touched the country picker
      if (!phone.countryCode) {
        // Posé par la validation, pas par le client : ne part qu'avec un
        // Continuer réussi (sinon un « +1 » sans numéro arrivait en base).
        updateValue({ ...phone, countryCode: "+1" }, { sansSauvegarde: true });
        phone.countryCode = "+1";
      }
      if (currentQuestion.isRequired) {
        const errs: Record<string, string> = {};
        if (
          !phone.countryCode?.trim() ||
          !/^\+?\d{1,4}$/.test(phone.countryCode.trim())
        ) {
          errs.countryCode = t.invalidCode;
        }
        if (
          !phone.number?.trim() ||
          phone.number.trim().replace(/\s/g, "").length < 7
        ) {
          errs.number = t.invalidNumber;
        }
        if (Object.keys(errs).length > 0) {
          setFieldErrors(errs);
          setError(t.fixErrors);
          return;
        }
      }
    } else if (currentQuestion.type === "travel-history") {
      const entries = data.travelHistory || [];
      const hasNoTravel = data.hasNoTravelHistory || false;
      if (!hasNoTravel && entries.length === 0) {
        setError(t.addTravel);
        return;
      }
      const incompleteEntry = entries.find(
        (e: TravelEntry) => !e.country || !e.startDate || !e.endDate,
      );
      if (incompleteEntry) {
        setError(t.completeTravel);
        return;
      }
    } else if (currentQuestion.type === "select") {
      if (currentQuestion.isRequired && !getCurrentValue()) {
        setError(t.selectOption);
        return;
      }
      const currentVal = getCurrentValue() as string;
      const isAutreValidation = currentVal === "autre" || currentQuestion.options?.find((o) => o.name === currentVal)?.code === "autre";
      if (
        isAutreValidation &&
        !readAnswer(data, currentQuestion.id + "_autrePrecision")?.toString().trim()
      ) {
        setError(t.specifyAnswer);
        return;
      }
    } else if (currentQuestion.type === "yes-no") {
      if (currentQuestion.isRequired && !(overrideValue ?? getCurrentValue())) {
        setError(t.answerRequired);
        return;
      }
    } else if (currentQuestion.type === "multi-entry") {
      const entries = (getCurrentValue() as any[]) || [];
      if (currentQuestion.isRequired && entries.length === 0) {
        setError(t.addEntry);
        return;
      }
      // Check each entry has required fields filled. Pour une liste répétée
      // par personne, seules les colonnes de la liste sont exigées ici ; les
      // autres sont posées ensuite, écran par écran.
      const listFields = isRepeatGroup(currentQuestion)
        ? rosterFields(currentQuestion)
        : currentQuestion.multiEntryFields;
      for (const entry of entries) {
        const emptyField = listFields?.find(
          (f) => f.required !== false && !entry[f.key]?.toString().trim(),
        );
        if (emptyField) {
          setError(t.fillField(emptyField.label));
          return;
        }
      }
    } else {
      const value = getCurrentValue();
      // Required check for text, textarea, number, date
      if (
        currentQuestion.isRequired &&
        currentQuestion.type !== "document" &&
        (value === undefined || value === null || value === "" ||
          (typeof value === "string" && !value.trim()))
      ) {
        setError(t.answerRequired);
        return;
      }
      if (currentQuestion.validation) {
        const validationError = currentQuestion.validation(value);
        if (validationError) {
          setError(validationError);
          return;
        }
      }
      // Block progression when Gemini detected the wrong document or a name mismatch and retries remain
      if (
        currentQuestion.type === "document" &&
        ocrStatus === "error" &&
        ocrResult?.error === "WRONG_DOCUMENT" &&
        ocrHasRetriesLeft
      ) {
        setError(ocrResult.wrongDocumentFeedback ?? t.wrongDocumentDefault);
        return;
      }
      if (
        currentQuestion.type === "document" &&
        ocrStatus === "error" &&
        ocrResult?.error === "REQUIREMENTS_NOT_MET" &&
        ocrHasRetriesLeft
      ) {
        setError(ocrResult.requirementsFeedback ?? t.requirementsNotMetDefault);
        return;
      }
      if (
        currentQuestion.type === "document" &&
        ocrStatus === "error" &&
        ocrResult?.error === "NAME_MISMATCH" &&
        ocrHasRetriesLeft
      ) {
        setError(ocrResult.nameMismatchFeedback ?? "Le nom sur le document ne correspond pas au nom du demandeur.");
        return;
      }
      // Required check for document
      if (
        currentQuestion.type === "document" &&
        currentQuestion.isRequired
      ) {
        const effectiveValue = overrideValue ?? value;
        const hasFile = effectiveValue === "__no_document__" || (Array.isArray(effectiveValue) ? effectiveValue.length > 0 : !!(pendingFile || effectiveValue));
        if (!hasFile) {
          setError(t.uploadRequired);
          return;
        }
      }
    }


    // When proceeding from a document step with OCR results, mark extracted fields for skipping
    if (currentQuestion.type === "document" && ocrStatus === "success" && ocrResult) {
      const extractedKeys = Object.keys(editableOcrFields);
      if (extractedKeys.length > 0) {
        // Apply the (possibly edited) OCR fields to data
        setData((prev) => mergeAnswers(prev, editableOcrFields));
        setOcrSkipFields((prev) => {
          const next = new Set(prev);
          extractedKeys.forEach((key) => next.add(key));
          return next;
        });
      }
    }

    // All validation passed — lock navigation until the transition completes
    isNavigating.current = true;

    // Commit pending file to data now that the user has proceeded past validation
    if (pendingFile && currentQuestion.type === "document") {
      const docId = currentQuestion.id;
      setData((prev) => (isFlatVirtual(currentQuestion) ? { ...prev, [docId]: pendingFile } : writeAnswer(prev, docId, pendingFile)));
      setPendingFile(null);
    }

    // The value to save: prefer overrideValue (from yes-no buttons), then pendingFile, then data
    const answerValue = (overrideValue !== undefined)
      ? overrideValue
      : (currentQuestion.type === "document" && pendingFile)
        ? pendingFile
        // L'historique de voyages s'édite sous des clés fixes ; la réponse de
        // la question, celle que la console lit, en est la liste.
        : currentQuestion.type === "travel-history"
          ? (data.hasNoTravelHistory ? [] : data.travelHistory ?? [])
          : getCurrentValue();

    // Save answer to DB. Un écran « message » n'a rien à enregistrer.
    if (currentQuestion.type !== "message") {
      // Bundle _details AND OCR-extracted fields into extras so everything is saved
      // in one atomic operation via saveInitialAnswers (avoids race condition where
      // concurrent saveAnswer calls all read the same stale state and overwrite each other)
      const detailsKey = `${currentQuestion.id}_details`;
      const extras: Record<string, any> = {};
      if (data[detailsKey] !== undefined) {
        extras[detailsKey] = data[detailsKey];
      }
      // La précision « autre, précisez » d'une liste, et l'historique de
      // voyages, vivent sous des clés à part : sans elles ici, aucun chemin ne
      // les envoyait. En prod, 77 précisions tapées par des clients ne sont
      // jamais arrivées en base (53 soumissions, mars → oct. 2026, scénario 12).
      // Comme la réponse principale, elles ne partent que si elles diffèrent de
      // ce que la base connaît : un onglet resté sur une vieille valeur
      // n'écrase pas la correction faite ailleurs (scénario 10). Une précision
      // laissée en base quand le client quitte « Autre » y reste, comme une
      // réponse masquée : on ne l'efface pas, on ne la renvoie pas.
      const siChange = (cle: string, valeur: unknown) => {
        if (!memeValeurQueLaBase(cle, valeur)) extras[cle] = valeur;
      };
      if (currentQuestion.type === "select" && !parseVirtualId(currentQuestion.id)) {
        const pk = `${currentQuestion.id}_autrePrecision`;
        const precision = data[pk];
        const valeur = getCurrentValue() as string;
        const surAutre =
          valeur === "autre" || currentQuestion.options?.find((o) => o.name === valeur)?.code === "autre";
        if (surAutre && typeof precision === "string" && precision.trim()) siChange(pk, precision);
      }
      if (currentQuestion.type === "travel-history") {
        siChange("travelHistory", data.travelHistory ?? []);
        siChange("hasNoTravelHistory", !!data.hasNoTravelHistory);
      }
      if (currentQuestion.type === "document" && ocrStatus === "success" && ocrResult) {
        for (const [key, value] of Object.entries(editableOcrFields)) {
          extras[key] = value;
        }
      }
      // Per-file verification verdict for the consultant's "À revoir" flag,
      // keyed by file signature (matches Index.tsx's lookup on upload).
      let verification: Record<string, { status: string; feedback?: string }> | undefined;
      if (currentQuestion.type === "document") {
        const FLAGGED = new Set(["WRONG_DOCUMENT", "REQUIREMENTS_NOT_MET", "NAME_MISMATCH"]);
        if (isMultiFile) {
          const map: Record<string, { status: string; feedback?: string }> = {};
          for (const [sig, v] of Object.entries(multiVerify)) {
            // « Vérifié » ne s'écrit que s'il a été CONSTATÉ. Une analyse encore
            // en cours n'est pas une analyse réussie : si le client continue
            // avant la fin — ce qui est le cas courant sur un gros fichier —
            // l'ancienne écriture l'enregistrait comme vérifié, et le verdict
            // qui arrivait ensuite n'atteignait plus jamais la base, la page
            // étant quittée. C'est exactement le défaut que ce correctif visait,
            // et il survivait dans ce chemin-ci.
            map[sig] =
              v.status === "flagged"
                ? { status: "flagged", feedback: v.feedback }
                : { status: v.status === "ok" ? "ok" : "unverified" };
          }
          if (Object.keys(map).length) verification = map;
        } else if (ocrResult && answerValue instanceof File) {
          const flagged = !!ocrResult.error && FLAGGED.has(ocrResult.error);
          // Une observation signale sans refuser : le document est accepté, le
          // client ne voit rien, et le cabinet retrouve le constat à côté de la
          // pièce. C'est le traitement qu'on réserve déjà au nom qui ne
          // correspond pas, étendu à tout ce qui relève du droit plutôt que de
          // la nature du document.
          const signalement =
            ocrResult.requirementsFeedback ??
            ocrResult.wrongDocumentFeedback ??
            ocrResult.nameMismatchFeedback ??
            ocrResult.observations;
          verification = {
            [fileSig(answerValue)]:
              flagged || (signalement && !estEchecTechnique(ocrResult))
                ? { status: "flagged", feedback: signalement }
                : { status: estEchecTechnique(ocrResult) ? "unverified" : "ok" },
          };
        }
      }
      annulerLaSauvegardeEnAttente();
      // Une pièce part toujours (son fichier et son verdict) ; une réponse
      // ordinaire que la base a déjà telle quelle ne repart pas : la renvoyer
      // écraserait ce qu'un autre onglet a corrigé entre-temps.
      // …sauf si la dernière chose partie pour elle est brute (sans
      // traduction) : le Continuer la renvoie alors complète.
      const rienDeNouveau =
        currentQuestion.type !== "document" &&
        Object.keys(extras).length === 0 &&
        !verification &&
        !envoyeesBrutes.current.has(currentQuestion.id) &&
        // Une case annexe modifiée sur cet écran (la précision « autre » d'une
        // personne d'une liste répétée part avec la liste entière) : à envoyer.
        annexeNonEnregistree.current !== currentQuestion.id &&
        memeValeurQueLaBase(currentQuestion.id, answerValue);
      if (!rienDeNouveau) {
        persistAnswer(
          currentQuestion.id,
          answerValue,
          Object.keys(extras).length > 0 ? extras : undefined,
          currentQuestion.type,
          currentQuestion.label,
          currentQuestion.multiEntryFields,
          verification,
        );
      }
    }

    isDirty.current = false;
    if (annexeNonEnregistree.current === currentQuestion.id) annexeNonEnregistree.current = null; // ses cases annexes sont parties avec ce Continuer

    // Show success feedback
    if (currentQuestion.type !== "message") {
      setShowSuccess(true);
      plusTard(() => setShowSuccess(false), 300);
    }

    // Proceed to next step
    setTimeout(() => {
      // Recalculate visible questions with updated data and skip fields
      const updatedSkipFields = new Set(ocrSkipFields);
      if (currentQuestion.type === "document" && ocrStatus === "success" && ocrResult) {
        Object.keys(editableOcrFields).forEach((key) => updatedSkipFields.add(key));
      }
      // The just-saved answer (and any OCR-extracted fields) live in queued setData
      // calls that haven't flushed into the `data` closure yet — for yes-no/checkbox
      // the value arrives via overrideValue. Recompute visibility against an effective
      // snapshot so conditional questions this answer unlocks (or hides) are reflected
      // immediately; otherwise navigation can skip a revealed branch or land on a blank step.
      const effectiveData = mergeAnswers(
        isFlatVirtual(currentQuestion)
          ? { ...data, [currentQuestion.id]: answerValue }
          : writeAnswer(data, currentQuestion.id, answerValue),
        currentQuestion.type === "document" && ocrStatus === "success" && ocrResult
          ? editableOcrFields
          : {},
      );
      const newVisible = getVisibleQuestions(effectiveData, updatedSkipFields);
      if (reviewModeQuestions) {
        // Answering this question may have unlocked new visible follow-up questions
        // that were invisible before (dependsOn not satisfied). Append them to the queue.
        const alreadyQueued = new Set(reviewModeQuestions.map(q => q.id));
        const newlyUnlocked = newVisible.filter(q => !isAnswered(q, effectiveData) && !alreadyQueued.has(q.id));
        const updatedReviewList = newlyUnlocked.length > 0
          ? [...reviewModeQuestions, ...newlyUnlocked]
          : reviewModeQuestions;
        if (newlyUnlocked.length > 0) {
          setReviewModeQuestions(updatedReviewList);
        }
        if (currentStepIndex < updatedReviewList.length - 1) {
          setCurrentStepIndex((prev) => prev + 1);
        } else {
          // Finished all review questions → back to review screen
          setReviewModeQuestions(null);
          setCurrentStepIndex(0);
          setShowReview(true);
        }
      } else if (editingFromReview.current) {
        // User edited a single question from the review screen → return to review
        editingFromReview.current = false;
        setShowReview(true);
      } else {
        // Find current question's position in the updated visible list.
        // Indices may shift when OCR adds fields to the skip set (e.g. fullName
        // removed after passport scan), so we locate by ID instead of relying
        // on the previous numeric index.
        const currentIdx = newVisible.findIndex(q => q.id === currentQuestion.id);
        const nextIdx = (currentIdx !== -1 ? currentIdx : currentStepIndex) + 1;
        if (nextIdx < newVisible.length) {
          setCurrentStepIndex(nextIdx);
        } else {
          setShowReview(true);
        }
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      releaseLockAfterRender.current = true;
    }, 200);
  };

  const handleEditQuestion = (questionId: string) => {
    const idx = visibleQuestions.findIndex(q => q.id === questionId);
    if (idx !== -1) {
      editingFromReview.current = true;
      setCurrentStepIndex(idx);
      setShowReview(false);
    }
  };

  const goBack = () => {
    if (isNavigating.current) return;
    isNavigating.current = true;
    clearAutoAdvance(); // cancel any pending forward auto-advance
    // Ce qui est tapé part avant de quitter l'écran, traduction comprise : la
    // valeur encore en attente, ou celle déjà partie brute.
    enregistrerCeQuiAttend({ complete: true });
    if (currentQuestion && envoyeesBrutes.current.has(currentQuestion.id)) {
      envoyeesBrutes.current.delete(currentQuestion.id);
      const valeur = getCurrentValue();
      if (!estVide(valeur)) {
        persistAnswer(currentQuestion.id, valeur, undefined, currentQuestion.type, currentQuestion.label, currentQuestion.multiEntryFields);
      }
    }
    if (editingFromReview.current) {
      // User was editing from review → back goes to review, not previous question
      editingFromReview.current = false;
      setShowReview(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      releaseLockAfterRender.current = true;
      return;
    }
    if (reviewModeQuestions) {
      if (currentStepIndex > 0) {
        setCurrentStepIndex((prev) => prev - 1);
      } else {
        // At first review-mode question → go back to review screen
        setReviewModeQuestions(null);
        setShowReview(true);
      }
    } else if (showReview) {
      setShowReview(false);
    } else if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
    releaseLockAfterRender.current = true;
  };

  // Single-flight auto-advance: cancels any previously-scheduled advance before
  // arming a new one, so a stale timer can never fire after the user moved on.
  const scheduleAutoAdvance = (value: any, delay: number) => {
    clearAutoAdvance();
    autoAdvanceTimer.current = window.setTimeout(() => {
      autoAdvanceTimer.current = null;
      validateAndProceed(value);
    }, delay);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (
      e.key === "Enter" &&
      (currentQuestion?.type === "text" || currentQuestion?.type === "number")
    ) {
      e.preventDefault();
      validateAndProceed();
    }
  };

  // Document upload handlers
  const isMultiFile = currentQuestion?.documentConfig?.maxFiles && currentQuestion.documentConfig.maxFiles > 1;
  // Per-file verification status in multi-file mode (keyed by a file signature).
  // Multi-file is lenient: we keep every file and just surface a flag for the
  // consultant, since blocking+retry per file across a bulk list is poor UX.
  const [multiVerify, setMultiVerify] = useState<
    Record<
      string,
      { status: "scanning" | "ok" | "flagged" | "unverified"; feedback?: string }
    >
  >({});
  // A multi-file answer restored from a saved submission holds storage ids
  // (strings) next to the Files added in this session.
  const fileSig = (f: File | string) => (typeof f === "string" ? f : `${f.name}|${f.size}|${f.lastModified}`);
  // Drag & drop state — desktop users expect to drop files onto the upload area.
  // Without this the only way in is the hidden <input type="file"> behind a button.
  const [isDragOver, setIsDragOver] = useState(false);

  const processFile = (file: File) => {
    const docConfig = currentQuestion?.documentConfig;
    const acceptedFormats = docConfig?.acceptedFormats ?? ['image/jpeg', 'image/png', 'application/pdf'];
    const maxSizeMB = docConfig?.maxSizeMB ?? 10;

    if (!acceptedFormats.includes(file.type)) {
      setError(t.fileFormatNotAccepted);
      return;
    }

    const maxSize = maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      setError(t.fileTooLarge(maxSizeMB));
      return;
    }

    setError(null);

    if (isMultiFile) {
      const existing = (getCurrentValue() as File[]) || [];
      const maxFiles = docConfig?.maxFiles || 5;
      if (existing.length >= maxFiles) {
        setError(`Maximum ${maxFiles} fichiers.`);
        return;
      }
      updateValue([...existing, file]);

      // Verify each file (non-blocking): run the same type + firm-criteria check
      // as single-file, but keep the file regardless and show a per-file badge.
      const ocrConfig = ocrConfigFor(currentQuestion);
      if (ocrConfig && (ocrConfig.expectedDocumentType || ocrConfig.verificationCriteria)) {
        const sig = fileSig(file);
        setMultiVerify((p) => ({ ...p, [sig]: { status: "scanning" } }));
        const clientName = expectedNameFor(currentQuestion);
        // `partOfSet` : les critères du cabinet décrivent l'exigence entière, et
        // on n'en examine qu'un fichier. Sans ce drapeau, chaque pièce d'une
        // paire se voit reprocher d'être seule.
        scanDocument(
          file,
          { ...ocrConfig, partOfSet: true },
          undefined,
          ocrCtx,
          clientName,
          preferredLanguage,
        )
          .then((r) => {
            const flagged = r.error === "WRONG_DOCUMENT" || r.error === "REQUIREMENTS_NOT_MET";
            // Le même signalement que pour un dépôt unique : ce qui relève du
            // droit se note, ne refuse pas. Sans cette ligne, un dépôt groupé
            // perdait l'observation en chemin.
            const signalement =
              r.requirementsFeedback ?? r.wrongDocumentFeedback ?? r.nameMismatchFeedback ?? r.observations;
            // Une panne de NOTRE côté n'est pas une vérification réussie. Le
            // dépôt reste accepté — on ne bloque personne parce que notre
            // lecture a échoué — mais on cesse d'afficher une coche verte sur un
            // document que Formio n'a jamais lu.
            const status = estEchecTechnique(r)
              ? "unverified"
              : flagged || signalement
                ? "flagged"
                : "ok";
            setMultiVerify((p) => ({
              ...p,
              [sig]: { status, feedback: status === "flagged" ? signalement : undefined },
            }));
          })
          .catch(() => {
            // Échec ouvert : ne jamais bloquer un dépôt groupé. Mais « non
            // vérifié », pas « vérifié ».
            setMultiVerify((p) => ({ ...p, [sig]: { status: "unverified" } }));
          });
      }
    } else {
      // Stage the file locally — don't commit to data until the user proceeds.
      // This prevents a rejected document from appearing as "accepted" if OCR state resets.
      setPendingFile(file);

      if (file.type.startsWith("image/")) {
        setPreviewUrl(URL.createObjectURL(file));
      }

      // Trigger OCR for document type verification (and field pre-filling when a full config exists).
      // Falls back to verification-only for document questions without a full OCR config.
      // Question IDs that belong to a third party (guarantor, etc.) — skip name check
      const THIRD_PARTY_QUESTION_IDS = new Set([
        'd3GuarantorPassport',
        'd3GuarantorStatusProof',
        'd3FiliationProof',
      ]);
      const isThirdPartyDoc = THIRD_PARTY_QUESTION_IDS.has(currentQuestion.id);

      const ocrConfig = ocrConfigFor(currentQuestion) ?? {
        documentType: currentQuestion.id,
        expectedDocumentType: currentQuestion.indication
          ? `${currentQuestion.label} — ${currentQuestion.indication}`
          : currentQuestion.label,
        prompt: 'Examine this document image.\n\nReturn ONLY the JSON, no markdown, no explanation.',
        fieldMapping: {},
        skipNameVerification: isThirdPartyDoc,
      };

      // Pass the applicant's name so Gemini can verify the document belongs to them.
      // Skip when the name hasn't been entered yet (e.g. passport is the first step).
      const clientName = expectedNameFor(currentQuestion);
      ocrScan(file, ocrConfig, ocrCtx, clientName, preferredLanguage);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    processFile(file);
    // Reset so the same file can be re-selected after clearing
    event.target.value = '';
  };

  // Drag & drop handlers — wire onto the upload-area wrapper.
  // dragenter/dragover must call preventDefault for drop to fire.
  const handleDragOver = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    if (!isDragOver) setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only flip off when the drag leaves the wrapper entirely, not when it
    // moves between child elements (relatedTarget will be inside on those).
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setIsDragOver(false);
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const clearFile = () => {
    setPendingFile(null);
    updateValue(null);
    setPreviewUrl(null);
    setConfirmed(false);
  };

  const removeFile = (index: number) => {
    const files = (getCurrentValue() as File[]) || [];
    const updated = files.filter((_, i) => i !== index);
    updateValue(updated.length > 0 ? updated : null);
    setConfirmingFileDelete(null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  // Review screen
  if (showReview) {
    return (
      <ReviewScreen
        data={data}
        questions={getVisibleQuestions(data).filter((q) => q.type !== "message")}
        onBack={goBack}
        onSubmit={(note) => {
          onComplete(note ? { ...data, [CLE_NOTE]: note } : data);
        }}
        onStartReviewMode={() => {
          const unanswered = getVisibleQuestions(data).filter(q => q.type !== "message" && !isAnswered(q, data));
          setReviewModeQuestions(unanswered);
          setCurrentStepIndex(0);
          setShowReview(false);
        }}
        onEditQuestion={handleEditQuestion}
        saveStatus={saveStatus}
        brandMark={brandMark}
        t={t}
        preferredLanguage={preferredLanguage}
        onLanguageChange={onLanguageChange}
      />
    );
  }

  if (showFeedback) {
    return (
      <FeedbackScreen
        uiT={t}
        onSubmit={(feedback) => {
          onFeedbackSave?.(feedback);
          setShowFeedback(false);
        }}
      />
    );
  }

  if (!currentQuestion) return null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="max-w-xl mx-auto w-full px-4 pt-4 pb-8 md:pb-12 flex flex-col flex-1">
        {/* Top header: brand mark + language switcher. Save & Quit was removed
            because every answer is autosaved (the indicator above the question
            already shows 'Sauvegardé') — the welcome screen now states this. */}
        <div className="flex justify-between items-center mb-3">
          <span className="text-sm font-semibold text-primary leading-none">
            {brandMark}
          </span>
          {onLanguageChange && preferredLanguage && (
            <LanguageSwitcher
              value={preferredLanguage}
              onChange={onLanguageChange}
            />
          )}
        </div>
        {/* Review mode banner */}
        {reviewModeQuestions && (
          <div className="mb-3 flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
            <span className="font-medium">{t.reviewMode}</span>
            <span className="text-amber-400">·</span>
            <span>
              {t.missingQuestions(currentStepIndex + 1, reviewModeQuestions.length)}
            </span>
          </div>
        )}

        {/* Milestone Progress */}
        <MilestoneProgress
          currentStepIndex={currentStepIndex}
          totalSteps={totalSteps}
          currentSection={currentSection}
          stepInSection={stepInSection}
          totalInSection={totalInSection}
          saveStatus={saveStatus}
          t={t}
        />

        {/* Section header */}
        {isNewSection && (
          <div className="mb-4 p-3 bg-primary/10 rounded-xl">
            <p className="text-sm font-semibold text-primary">
              {currentSection}
            </p>
          </div>
        )}

        {/* Main Card */}
        <div className="bg-card rounded-2xl shadow-card p-6 md:p-8">
          {/* Title */}
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
            {currentQuestion.label}
          </h1>

          {/* Pre-fill badge */}
          {prefilledFields.has(currentQuestion.id) && getCurrentValue() && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg mb-4 w-fit">
              <ScanLine className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-medium text-blue-700">
                {t.prefilledByScan}
              </span>
            </div>
          )}

          {/* Instructions.
              La case unique de consentement réutilise `indication` comme libellé
              à côté de la case : l'afficher ici en plus la répétait mot pour mot
              juste au-dessus. On la laisse au bloc de la case dans ce cas. */}
          {!(currentQuestion.type === "checkbox" && !isMultiChoiceCheckbox(currentQuestion)) && (
            <div className="space-y-1 mb-4">
              {/* `whitespace-pre-line` : un cabinet écrit ses consignes en
                  liste — « Cela inclut : » suivi de sept situations, une par
                  ligne. Sans ça, tout se collait en un seul pavé, « détenu(e)
                  Avoir fait l'objet », illisible pour la personne à qui on
                  demande précisément de ne rien oublier. */}
              <p className="text-base text-foreground whitespace-pre-line">
                {currentQuestion.indication}
              </p>
            </div>
          )}

          {/* Visual Anchor */}
          {currentQuestion.help &&
            currentQuestion.type !== "document" &&
            // Masqué seulement pour la case unique de consentement (son bloc
            // porte déjà tout le texte) ; les choix multiples gardent l'aide.
            (currentQuestion.type !== "checkbox" || isMultiChoiceCheckbox(currentQuestion)) && (
              <div className="flex items-center gap-2 p-3 bg-secondary rounded-xl mb-6">
                <HelpCircle className="w-4 h-4 text-primary" />
                <p className="text-sm text-primary font-medium">
                  {currentQuestion.help}
                </p>
              </div>
            )}

          {/* Success Feedback */}
          {showSuccess && (
            <div className="flex items-center gap-2 p-3 bg-success/10 rounded-xl mb-6 animate-in fade-in duration-200">
              <CheckCircle2 className="w-5 h-5 text-success" />
              <span className="text-sm font-medium text-success">
                {currentQuestion.successMessage}
              </span>
            </div>
          )}

          {/* Error */}
          {error && (
            <div id="question-error" role="alert" className="flex items-center gap-2 p-3 bg-destructive/10 rounded-xl mb-6">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              <span className="text-sm text-destructive">{error}</span>
            </div>
          )}

          {/* Input based on type */}
          <div className="mb-6">
            {currentQuestion.type === "text" && (
              <div>
                <Input
                  ref={textInputRef}
                  value={(getCurrentValue() as string) || ""}
                  onChange={(e) => updateValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={currentQuestion.placeholder}
                  className="h-14 text-lg px-4"
                  aria-describedby={error ? "question-error" : undefined}
                />
                {currentQuestion.example && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {t.examplePrefix}{currentQuestion.example}
                  </p>
                )}
                {currentQuestion.detailsBoxLabel && (
                  <div className="mt-3">
                    <label className="text-sm text-muted-foreground mb-1 block">
                      {currentQuestion.detailsBoxLabel}
                    </label>
                    <Textarea
                      value={(data[`${currentQuestion.id}_details`] as string) || ""}
                      onChange={(e) => {
                        // Part au Continuer seulement : d'ici là, c'est du non-enregistré.
                        annexeNonEnregistree.current = currentQuestion.id;
                        setData((prev) => ({ ...prev, [`${currentQuestion.id}_details`]: e.target.value }));
                      }}
                      placeholder={t.specifyPlaceholder}
                      className="text-base resize-none"
                      rows={3}
                    />
                  </div>
                )}
              </div>
            )}

            {currentQuestion.type === "number" && (
              <div>
                <Input
                  ref={textInputRef}
                  type="number"
                  value={(getCurrentValue() as string) || ""}
                  onChange={(e) => updateValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={currentQuestion.placeholder}
                  className="h-14 text-lg px-4"
                  aria-describedby={error ? "question-error" : undefined}
                />
                {currentQuestion.example && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {t.examplePrefix}{currentQuestion.example}
                  </p>
                )}
              </div>
            )}

            {currentQuestion.type === "textarea" && (
              <div>
                <Textarea
                  ref={textareaRef}
                  value={(getCurrentValue() as string) || ""}
                  onChange={(e) => updateValue(e.target.value)}
                  placeholder={currentQuestion.placeholder}
                  className="min-h-[120px] text-base px-4 py-3"
                  aria-describedby={error ? "question-error" : undefined}
                />
                {currentQuestion.example && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {t.examplePrefix}{currentQuestion.example}
                  </p>
                )}
              </div>
            )}

            {currentQuestion.type === "address" && (
              <div className="space-y-4">
                <AddressAutocomplete
                  onAddressSelected={(addr) => {
                    setFieldErrors({});
                    setAutocompleteAddress(addr);
                    setShowManualAddress(false);
                    updateValue({
                      ...((getCurrentValue() as any) || {}),
                      street: addr.street,
                      city: addr.city,
                      province: addr.province,
                      country: addr.country,
                      postalCode: addr.postalCode,
                    });
                  }}
                  onManualEntry={() => {
                    setAutocompleteAddress(null);
                    setShowManualAddress(true);
                  }}
                  selectedAddress={autocompleteAddress}
                  placeholder={t.searchAddress}
                  manualEntryLabel={t.manualAddressLink}
                  editLabel={t.editAddress}
                  language={preferredLanguage}
                />
                {showManualAddress && (
                  <>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">
                        {t.streetLabel}
                      </label>
                      <Input
                        value={(getCurrentValue() as any)?.street || ""}
                        onChange={(e) => {
                          clearFieldError('street');
                          updateValue({
                            ...((getCurrentValue() as any) || {}),
                            street: e.target.value,
                          });
                        }}
                        onBlur={(e) => validateFieldOnBlur('street', e.target.value, "l'adresse civique")}
                        placeholder={t.streetPlaceholder}
                        className={cn("h-12", fieldErrors.street && "border-destructive")}
                      />
                      {fieldErrors.street && (
                        <p className="text-xs text-destructive mt-1">{fieldErrors.street}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">
                        {t.aptLabel}
                      </label>
                      <Input
                        value={(getCurrentValue() as any)?.apt || ""}
                        onChange={(e) =>
                          updateValue({
                            ...((getCurrentValue() as any) || {}),
                            apt: e.target.value,
                          })
                        }
                        placeholder={t.aptPlaceholder}
                        className="h-12"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">
                          {t.cityLabel}
                        </label>
                        <Input
                          value={(getCurrentValue() as any)?.city || ""}
                          onChange={(e) => {
                            clearFieldError('city');
                            updateValue({
                              ...((getCurrentValue() as any) || {}),
                              city: e.target.value,
                            });
                          }}
                          onBlur={(e) => validateFieldOnBlur('city', e.target.value, "la ville")}
                          placeholder={t.cityPlaceholder}
                          className={cn("h-12", fieldErrors.city && "border-destructive")}
                        />
                        {fieldErrors.city && (
                          <p className="text-xs text-destructive mt-1">{fieldErrors.city}</p>
                        )}
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">
                          {t.provinceLabel}
                        </label>
                        <Input
                          value={(getCurrentValue() as any)?.province || ""}
                          onChange={(e) => {
                            clearFieldError('province');
                            updateValue({
                              ...((getCurrentValue() as any) || {}),
                              province: e.target.value,
                            });
                          }}
                          onBlur={(e) => validateFieldOnBlur('province', e.target.value, "la province")}
                          placeholder={t.provincePlaceholder}
                          className={cn("h-12", fieldErrors.province && "border-destructive")}
                        />
                        {fieldErrors.province && (
                          <p className="text-xs text-destructive mt-1">{fieldErrors.province}</p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">
                          {t.countryLabel}
                        </label>
                        <CountryCombobox
                          value={(getCurrentValue() as any)?.country || ""}
                          onValueChange={(val) => {
                            clearFieldError('country');
                            updateValue({
                              ...((getCurrentValue() as any) || {}),
                              country: val,
                            });
                          }}
                          searchPlaceholder={t.searchCountry}
                          language={preferredLanguage}
                        />
                        {fieldErrors.country && (
                          <p className="text-xs text-destructive mt-1">{fieldErrors.country}</p>
                        )}
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">
                          {t.postalCodeLabel}
                        </label>
                        <Input
                          value={(getCurrentValue() as any)?.postalCode || ""}
                          onChange={(e) => {
                            clearFieldError('postalCode');
                            updateValue({
                              ...((getCurrentValue() as any) || {}),
                              postalCode: e.target.value,
                            });
                          }}
                          onBlur={(e) => validateFieldOnBlur('postalCode', e.target.value, "le code postal")}
                          placeholder={t.postalCodePlaceholder}
                          className={cn("h-12", fieldErrors.postalCode && "border-destructive")}
                        />
                        {fieldErrors.postalCode && (
                          <p className="text-xs text-destructive mt-1">{fieldErrors.postalCode}</p>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {currentQuestion.type === "phone" && (
              <div className="flex flex-col gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">
                    {t.phoneCountryLabel}
                  </label>
                  <PhoneCountrySelect
                    value={(getCurrentValue() as any)?.countryCode || "+1"}
                    onValueChange={(dialCode) => {
                      clearFieldError('countryCode');
                      updateValue({
                        ...((getCurrentValue() as any) || { countryCode: "+1" }),
                        countryCode: dialCode,
                      });
                    }}
                    hasError={!!fieldErrors.countryCode}
                    language={preferredLanguage}
                  />
                  {fieldErrors.countryCode && (
                    <p className="text-xs text-destructive mt-1">{fieldErrors.countryCode}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">
                    {t.phoneNumberLabel}
                  </label>
                  <Input
                    value={(getCurrentValue() as any)?.number || ""}
                    onChange={(e) => {
                      clearFieldError('number');
                      const digits = e.target.value.replace(/[^\d]/g, "").slice(0, 15);
                      const formatted = digits.replace(/^(\d{3})(\d{0,3})(\d{0,4})(.*)$/, (_, a, b, c, d) => [a, b, c, d].filter(Boolean).join(" ")).trim();
                      updateValue({
                        ...((getCurrentValue() as any) || { countryCode: "+1" }),
                        number: formatted,
                      });
                    }}
                    onBlur={(e) => {
                      if (!currentQuestion?.isRequired) return;
                      const v = e.target.value.trim().replace(/\s/g, "");
                      if (!v || v.length < 7) {
                        setFieldErrors((prev) => ({ ...prev, number: t.invalidNumber }));
                      } else {
                        clearFieldError('number');
                      }
                    }}
                    type="tel"
                    inputMode="tel"
                    placeholder="514 555 1234"
                    className={cn("h-14 text-lg", fieldErrors.number && "border-destructive")}
                  />
                  {fieldErrors.number && (
                    <p className="text-xs text-destructive mt-1">{fieldErrors.number}</p>
                  )}
                </div>
              </div>
            )}

            {currentQuestion.type === "yes-no" && (
              <div className="flex flex-col gap-3" role="group" aria-label={currentQuestion.label} aria-describedby={error ? "question-error" : undefined}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => {
                      updateValue("oui");
                      const needsDetails = currentQuestion.detailsBoxLabel;
                      const needsConfirm = currentQuestion.requiresConfirmation;
                      if (!needsDetails && !needsConfirm) {
                        scheduleAutoAdvance("oui", 350);
                      }
                    }}
                    aria-pressed={getCurrentValue() === "oui"}
                    className={cn(
                      "min-h-[56px] p-4 rounded-xl border-2 text-lg font-medium transition-all",
                      getCurrentValue() === "oui"
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border hover:border-primary/50 text-foreground",
                    )}
                  >
                    {t.yes}
                  </button>
                  <button
                    onClick={() => {
                      updateValue("non");
                      if (!currentQuestion.requiresConfirmation) {
                        scheduleAutoAdvance("non", 350);
                      }
                    }}
                    aria-pressed={getCurrentValue() === "non"}
                    className={cn(
                      "min-h-[56px] p-4 rounded-xl border-2 text-lg font-medium transition-all",
                      getCurrentValue() === "non"
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border hover:border-primary/50 text-foreground",
                    )}
                  >
                    {t.no}
                  </button>
                </div>
                {currentQuestion.detailsBoxLabel && getCurrentValue() === "oui" && (
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">
                      {currentQuestion.detailsBoxLabel}
                    </label>
                    <Textarea
                      value={(data[`${currentQuestion.id}_details`] as string) || ""}
                      onChange={(e) => {
                        // Part au Continuer seulement : d'ici là, c'est du non-enregistré.
                        annexeNonEnregistree.current = currentQuestion.id;
                        setData((prev) => ({ ...prev, [`${currentQuestion.id}_details`]: e.target.value }));
                      }}
                      placeholder={t.specifyPlaceholder}
                      className="text-base resize-none"
                      rows={3}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Choix multiples : une case par option, réponse = tableau de codes.
                Pas d'avance automatique — le client doit pouvoir cocher
                plusieurs options avant de passer à la suite. */}
            {isMultiChoiceCheckbox(currentQuestion) && (
              <div className="space-y-3">
                {currentQuestion.options?.map((option) => {
                  const selected = asCheckboxCodes(getCurrentValue());
                  const isChecked = selected.includes(option.code);
                  return (
                    <label
                      key={option.code}
                      className="flex items-start gap-3 p-4 bg-secondary/50 rounded-xl cursor-pointer hover:bg-secondary transition-colors"
                    >
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={(checked) => {
                          const next = checked
                            ? [...selected, option.code]
                            : selected.filter((c) => c !== option.code);
                          updateValue(next);
                        }}
                        className="mt-0.5"
                      />
                      <span className="text-base text-foreground leading-relaxed">
                        {getLocalizedOptionLabel(option, preferredLanguage)}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}

            {currentQuestion.type === "checkbox" && !isMultiChoiceCheckbox(currentQuestion) && (
              <div className="p-4 bg-secondary/50 rounded-xl">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={(getCurrentValue() as boolean) || false}
                    onCheckedChange={(checked) => {
                      updateValue(checked as boolean);
                      if (checked) {
                        // Pass overrideValue so validateAndProceed doesn't read stale state
                        scheduleAutoAdvance(true, 400);
                      }
                    }}
                    className="mt-0.5"
                  />
                  <span className="text-sm text-foreground leading-relaxed whitespace-pre-line">
                    {currentQuestion.indication}
                  </span>
                </label>
              </div>
            )}

            {currentQuestion.type === "date" && (
              // `key` : chaque question de date a son propre calendrier, comme
              // la liste déroulante plus bas. Durcissement, pas correctif —
              // contrairement à la liste, le bouton affiche `getCurrentValue()`
              // et suit donc la réponse, l'état interne de Radix ne peut pas
              // mentir ici de la même façon.
              //
              // Un cabinet a rapporté le 2026-10-09 que les dates d'entrée au
              // Canada restaient en rouge et qu'il fallait les effacer puis les
              // ressaisir. CE N'EST PAS EXPLIQUÉ. Déjà écarté : `updateValue`
              // appelle `setError(null)`, donc choisir une date efface l'erreur ;
              // lecture et écriture passent toutes deux par `readAnswer` /
              // `writeAnswer` pour ces questions (`isFlatVirtual` est faux, elles
              // ne sont pas dans une liste) ; et la validation ne refuse qu'une
              // valeur absente. Reste à regarder : la valeur d'une date de
              // premier niveau est rangée en objet `Date` brut — `toRowValue` ne
              // normalise qu'en ligne de liste — et ce que ça devient à
              // l'enregistrement puis au rechargement.
              <Popover key={currentQuestion.id}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full h-14 justify-start text-left font-normal text-lg px-4",
                      !getCurrentValue() && "text-muted-foreground",
                    )}
                    aria-describedby={error ? "question-error" : undefined}
                  >
                    <CalendarIcon className="mr-3 h-5 w-5" />
                    {getCurrentValue()
                      ? formatDateLocalized(getCurrentValue() as any, preferredLanguage)
                      : t.selectDate}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-card" align="start">
                  <Calendar
                    mode="single"
                    selected={toDate(getCurrentValue())}
                    onSelect={(date) => updateValue(date)}
                    initialFocus
                    captionLayout="dropdown-buttons"
                    fromYear={1920}
                    toYear={new Date().getFullYear() + 15}
                    className="p-3 pointer-events-auto"
                    locale={getDateFnsLocale(preferredLanguage)}
                  />
                </PopoverContent>
              </Popover>
            )}

            {currentQuestion.type === "select" && (
              <div className="space-y-3">
                {(currentQuestion.options?.length ?? 0) > 20 ? (
                  <CountryCombobox
                    value={(getCurrentValue() as string) || ""}
                    onValueChange={(val) => {
                      updateValue(val);
                      if (val !== "autre") {
                        setData((prev) => {
                          const pk = currentQuestion.id + "_autrePrecision";
                          if (parseVirtualId(pk)) return writeAnswer(prev, pk, undefined);
                          const next = { ...prev };
                          delete next[pk];
                          return next;
                        });
                      }
                    }}
                    className="h-14 text-lg px-4"
                    searchPlaceholder={t.searchCountry}
                    language={preferredLanguage}
                  />
                ) : (
                  // `key` : sans lui, React réutilise la MÊME liste déroulante
                  // d'une question à l'autre. Radix garde alors le libellé
                  // affiché de la question précédente, alors que la réponse,
                  // elle, est vide : le client lit « Oui », clique Continuer,
                  // et se fait répondre « Veuillez sélectionner une option »
                  // — sans moyen d'en sortir, puisque re-choisir la même valeur
                  // ne déclenche aucun changement. Une liste par question.
                  <Select
                    key={currentQuestion.id}
                    value={(() => {
                      const raw = getCurrentValue() as string;
                      if (!raw) return undefined;
                      // Backwards compat: if stored value is a name, find its code for the Select
                      const byName = currentQuestion.options?.find((o) => o.name === raw);
                      if (byName) return byName.code;
                      return raw; // old code values still work
                    })()}
                    onValueChange={(code) => {
                      updateValue(code);
                      // Clear precision when switching away from "autre"
                      if (code !== "autre") {
                        setData((prev) => {
                          const pk = currentQuestion.id + "_autrePrecision";
                          if (parseVirtualId(pk)) return writeAnswer(prev, pk, undefined);
                          const next = { ...prev };
                          delete next[pk];
                          return next;
                        });
                      }
                    }}
                  >
                    <SelectTrigger className="h-14 text-lg px-4">
                      <SelectValue placeholder={t.selectPlaceholder} />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {currentQuestion.options?.filter((option) => option.code && option.name).map((option) => (
                        <SelectItem key={option.code} value={option.code}>
                          {getLocalizedOptionLabel(option, preferredLanguage)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {(() => {
                  const raw = getCurrentValue() as string;
                  const isAutre = raw === "autre" || currentQuestion.options?.find((o) => o.name === raw)?.code === "autre";
                  return isAutre;
                })() && (
                  <Input
                    placeholder={t.specifyPlaceholder}
                    value={(readAnswer(data, currentQuestion.id + "_autrePrecision") as string) || ""}
                    onChange={(e) => {
                      const pk = currentQuestion.id + "_autrePrecision";
                      const text = e.target.value;
                      // Part au Continuer, avec la réponse (voir `extras`) : d'ici
                      // là, c'est du non-enregistré.
                      annexeNonEnregistree.current = currentQuestion.id;
                      setData((prev) => writeAnswer(prev, pk, text));
                    }}
                    className="h-14 text-lg px-4"
                  />
                )}
              </div>
            )}

            {currentQuestion.type === "travel-history" && (
              <TravelHistoryInput
                entries={data.travelHistory || []}
                hasNoTravel={data.hasNoTravelHistory || false}
                onEntriesChange={(entries) => {
                  // Part au Continuer (voir `extras`) : d'ici là, non enregistré.
                  annexeNonEnregistree.current = currentQuestion.id;
                  setData((prev) => ({ ...prev, travelHistory: entries }));
                }}
                onNoTravelChange={(hasNoTravel) => {
                  annexeNonEnregistree.current = currentQuestion.id;
                  setData((prev) => ({
                    ...prev,
                    hasNoTravelHistory: hasNoTravel,
                  }));
                }}
                language={preferredLanguage}
              />
            )}

            {currentQuestion.type === "multi-entry" &&
              currentQuestion.multiEntryFields && (() => {
                const addLabel =
                  (currentQuestion.multiEntryAddKey && t[currentQuestion.multiEntryAddKey]) ??
                  currentQuestion.multiEntryAddLabel ??
                  t.add;
                return (
                  <MultiEntryInput
                    entries={Array.isArray(getCurrentValue()) ? getCurrentValue() as any[] : []}
                    fields={isRepeatGroup(currentQuestion) ? rosterFields(currentQuestion) : currentQuestion.multiEntryFields}
                    addLabel={addLabel as string}
                    onChange={(entries) => updateValue(entries)}
                    selectPlaceholder={t.selectPlaceholder}
                    deleteConfirm={t.deleteConfirm}
                    entryLabel={isRepeatGroup(currentQuestion) && isPersonGroup(currentQuestion) ? t.repeatPerson : t.entry}
                    confirmDeleteAria={t.confirmDeleteEntryAria}
                    removeAria={t.removeEntryAria}
                    language={preferredLanguage}
                    premiereLigne={premiereLignePreRemplie(
                      currentQuestion.id,
                      data as Record<string, unknown>,
                      isRepeatGroup(currentQuestion) ? rosterFields(currentQuestion) : currentQuestion.multiEntryFields,
                    )}
                  />
                );
              })()}

            {currentQuestion.type === "document" && (
              <div>
                {/* L'exemple écrit par le cabinet. Il ne s'affichait que pour
                    les champs texte et nombre : un cabinet pouvait le rédiger
                    pour un document et le client ne le voyait jamais. */}
                {currentQuestion.example && (
                  <p className="text-xs text-muted-foreground mb-3">
                    {t.examplePrefix}{currentQuestion.example}
                  </p>
                )}
                {isMultiFile ? (
                  /* ── Multi-file mode ── */
                  <div>
                    {/* List of uploaded files */}
                    {Array.isArray(getCurrentValue()) && (getCurrentValue() as Array<File | string>).length > 0 && (
                      <div className="space-y-2 mb-4">
                        {(getCurrentValue() as Array<File | string>).map((file, idx) => (
                          <div key={idx} className="p-3 bg-secondary rounded-xl">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                                <FileImage className="w-5 h-5 text-primary" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">
                                  {file instanceof File ? file.name : t.fileAlreadyUploaded}
                                </p>
                                <div className="flex items-center gap-2 flex-wrap">
                                  {file instanceof File && (
                                    <p className="text-xs text-muted-foreground">
                                      {formatFileSize(file.size)}
                                    </p>
                                  )}
                                  {(() => {
                                    const v = multiVerify[fileSig(file)];
                                    if (!v) return null;
                                    if (v.status === "scanning")
                                      return (
                                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                                          <Loader2 className="w-3 h-3 animate-spin" />
                                          {t.ocrVerifying}
                                        </span>
                                      );
                                    if (v.status === "ok")
                                      return (
                                        <span className="text-xs text-success flex items-center gap-1">
                                          <CheckCircle2 className="w-3 h-3" />
                                          {t.ocrVerified}
                                        </span>
                                      );
                                    // Ni vert ni alarmant : le document est bien
                                    // reçu, c'est notre lecture qui n'a pas eu
                                    // lieu. Rien à faire pour le client.
                                    if (v.status === "unverified")
                                      return (
                                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                                          <FileText className="w-3 h-3" />
                                          {t.ocrUnverified}
                                        </span>
                                      );
                                    return (
                                      <span
                                        title={v.feedback}
                                        className="text-xs text-amber-600 flex items-center gap-1"
                                      >
                                        <AlertTriangle className="w-3 h-3" />
                                        {t.ocrFlagged}
                                      </span>
                                    );
                                  })()}
                                </div>
                                {(() => {
                                  const v = multiVerify[fileSig(file)];
                                  return v?.status === "flagged" && v.feedback ? (
                                    <p className="text-xs text-amber-700 mt-1 leading-snug">{v.feedback}</p>
                                  ) : null;
                                })()}
                              </div>
                              {confirmingFileDelete === idx ? (
                                <button
                                  onClick={() => removeFile(idx)}
                                  onBlur={() => setConfirmingFileDelete(null)}
                                  autoFocus
                                  className="px-2 py-1 bg-destructive text-destructive-foreground text-xs font-medium rounded-lg transition-colors"
                                  aria-label={t.confirmDeleteFileAria(idx + 1)}
                                >
                                  {t.deleteConfirm}
                                </button>
                              ) : (
                                <button
                                  onClick={() => setConfirmingFileDelete(idx)}
                                  className="p-1.5 hover:bg-muted rounded-lg transition-colors"
                                  aria-label={t.removeFileAria(idx + 1)}
                                >
                                  <Trash2 className="w-4 h-4 text-muted-foreground" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add file button (or initial upload prompt) */}
                    {(() => {
                      const files = (getCurrentValue() as File[]) || [];
                      const maxFiles = currentQuestion.documentConfig?.maxFiles || 5;
                      const canAdd = files.length < maxFiles;

                      if (files.length === 0) {
                        return (
                          <>
                            <div
                              className={cn(
                                "relative rounded-2xl border-2 border-dashed transition-colors p-5 sm:p-6",
                                isDragOver
                                  ? "border-primary bg-primary/5 ring-2 ring-primary ring-offset-2"
                                  : "border-border bg-secondary/40 hover:border-primary/40",
                              )}
                              onDragOver={handleDragOver}
                              onDragLeave={handleDragLeave}
                              onDrop={handleFileDrop}
                            >
                              <div className="absolute top-3 left-3 w-5 h-5 border-l-2 border-t-2 border-primary rounded-tl pointer-events-none" />
                              <div className="absolute top-3 right-3 w-5 h-5 border-r-2 border-t-2 border-primary rounded-tr pointer-events-none" />
                              <div className="absolute bottom-3 left-3 w-5 h-5 border-l-2 border-b-2 border-primary rounded-bl pointer-events-none" />
                              <div className="absolute bottom-3 right-3 w-5 h-5 border-r-2 border-b-2 border-primary rounded-br pointer-events-none" />
                              {isDragOver && (
                                <div className="absolute inset-0 z-10 flex items-center justify-center bg-primary/10 rounded-2xl pointer-events-none">
                                  <div className="bg-card px-4 py-3 rounded-xl shadow-card flex items-center gap-2">
                                    <Upload className="w-5 h-5 text-primary" aria-hidden="true" />
                                    <span className="text-base font-medium text-primary">
                                      {t.dropFileHere}
                                    </span>
                                  </div>
                                </div>
                              )}
                              <div className="space-y-3">
                                <button
                                  onClick={() => cameraInputRef.current?.click()}
                                  className="w-full p-4 bg-card border border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-colors flex items-center gap-4"
                                >
                                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                                    <Camera className="w-6 h-6 text-primary" />
                                  </div>
                                  <div className="text-left">
                                    <p className="font-medium text-foreground">{t.takePhoto}</p>
                                    <p className="text-sm text-muted-foreground">{t.useCamera}</p>
                                  </div>
                                </button>
                                <input
                                  ref={cameraInputRef}
                                  type="file"
                                  accept="image/*"
                                  capture="environment"
                                  onChange={handleFileSelect}
                                  className="hidden"
                                />
                                <button
                                  onClick={() => fileInputRef.current?.click()}
                                  className="w-full p-4 bg-card border border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-colors flex items-center gap-4"
                                >
                                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                                    <Upload className="w-6 h-6 text-primary" />
                                  </div>
                                  <div className="text-left">
                                    <p className="font-medium text-foreground">{t.uploadFile}</p>
                                    <p className="text-sm text-muted-foreground">
                                      {t.uploadFileDesc(currentQuestion.documentConfig?.maxSizeMB ?? 10)}
                                    </p>
                                  </div>
                                </button>
                                <input
                                  ref={fileInputRef}
                                  type="file"
                                  accept={currentQuestion.documentConfig?.acceptedFormats.join(",")}
                                  onChange={handleFileSelect}
                                  className="hidden"
                                />
                              </div>
                              <p className="hidden md:block text-center text-xs text-muted-foreground mt-3">
                                {t.orDragAndDrop}
                              </p>
                            </div>

                            <button
                              onClick={() => {
                                if (!currentQuestion) return;
                                const qid = currentQuestion.id;
                                setSkipReason("");
                                setSkipDialog({
                                  qid,
                                  isDoc: true,
                                  proceed: () => {
                                    updateValue("__no_document__");
                                    validateAndProceed("__no_document__");
                                  },
                                });
                              }}
                              type="button"
                              className="w-full text-center min-h-[44px] py-2.5 mt-3 text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-4 decoration-muted-foreground/60 hover:decoration-foreground"
                            >
                              {t.noDocument}
                            </button>
                          </>
                        );
                      }

                      return (
                        <div
                          className={cn(
                            "relative rounded-2xl border-2 border-dashed transition-colors p-4 sm:p-5",
                            isDragOver
                              ? "border-primary bg-primary/5 ring-2 ring-primary ring-offset-2"
                              : "border-border bg-secondary/40 hover:border-primary/40",
                          )}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleFileDrop}
                        >
                          {/* Corner brackets — match empty-state drop zone affordance */}
                          <div className="absolute top-3 left-3 w-5 h-5 border-l-2 border-t-2 border-primary rounded-tl pointer-events-none" />
                          <div className="absolute top-3 right-3 w-5 h-5 border-r-2 border-t-2 border-primary rounded-tr pointer-events-none" />
                          <div className="absolute bottom-3 left-3 w-5 h-5 border-l-2 border-b-2 border-primary rounded-bl pointer-events-none" />
                          <div className="absolute bottom-3 right-3 w-5 h-5 border-r-2 border-b-2 border-primary rounded-br pointer-events-none" />

                          {isDragOver && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-primary/10 rounded-2xl pointer-events-none">
                              <div className="bg-card px-4 py-3 rounded-xl shadow-card flex items-center gap-2">
                                <Upload className="w-5 h-5 text-primary" aria-hidden="true" />
                                <span className="text-base font-medium text-primary">
                                  {t.dropFileHere}
                                </span>
                              </div>
                            </div>
                          )}
                          {canAdd && (
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              className="w-full p-3 bg-card border border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-colors flex items-center justify-center gap-2"
                            >
                              <Plus className="w-5 h-5 text-primary" />
                              <span className="text-sm font-medium text-primary">
                                {t.addFile(files.length, maxFiles)}
                              </span>
                            </button>
                          )}
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept={currentQuestion.documentConfig?.acceptedFormats.join(",")}
                            onChange={handleFileSelect}
                            className="hidden"
                          />
                          <div className="flex items-center gap-2 text-success text-sm mt-3">
                            <CheckCircle2 className="w-4 h-4" />
                            <span>{t.documentsUploaded(files.length)}</span>
                          </div>
                          <p className="hidden md:block text-center text-xs text-muted-foreground mt-3">
                            {t.orDragAndDrop}
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  /* ── Single-file mode (original) ── */
                  <div>
                    {/* Upload buttons or preview */}
                    {!(pendingFile || getCurrentValue()) ? (
                      <>
                        {/*
                          Unified drop zone. The drop zone IS the visual target —
                          dashed border + corner brackets read as 'place document
                          here'. Buttons live inside as alternative input methods.
                          Replaces the old decorative placeholder which visually
                          competed with the upload area and confused users about
                          where to drop.
                        */}
                        <div
                          className={cn(
                            "relative rounded-2xl border-2 border-dashed transition-colors p-5 sm:p-6",
                            isDragOver
                              ? "border-primary bg-primary/5 ring-2 ring-primary ring-offset-2"
                              : "border-border bg-secondary/40 hover:border-primary/40",
                          )}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleFileDrop}
                        >
                          {/* Corner brackets — visually anchor it as a "drop zone" */}
                          <div className="absolute top-3 left-3 w-5 h-5 border-l-2 border-t-2 border-primary rounded-tl pointer-events-none" />
                          <div className="absolute top-3 right-3 w-5 h-5 border-r-2 border-t-2 border-primary rounded-tr pointer-events-none" />
                          <div className="absolute bottom-3 left-3 w-5 h-5 border-l-2 border-b-2 border-primary rounded-bl pointer-events-none" />
                          <div className="absolute bottom-3 right-3 w-5 h-5 border-r-2 border-b-2 border-primary rounded-br pointer-events-none" />

                          {/* Drop overlay — visible only while a file is being dragged over */}
                          {isDragOver && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-primary/10 rounded-2xl pointer-events-none">
                              <div className="bg-card px-4 py-3 rounded-xl shadow-card flex items-center gap-2">
                                <Upload className="w-5 h-5 text-primary" aria-hidden="true" />
                                <span className="text-base font-medium text-primary">
                                  {t.dropFileHere}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* idPhoto only: small head silhouette as framing guide */}
                          {currentQuestion?.id === 'idPhoto' && (
                            <div className="flex flex-col items-center mb-4">
                              <div
                                className="relative w-24 bg-card rounded-lg overflow-hidden border border-border"
                                style={{ aspectRatio: '35/45' }}
                              >
                                <div className="absolute inset-0 bg-gradient-to-b from-secondary to-muted flex items-end justify-center pb-0 overflow-hidden">
                                  <svg
                                    viewBox="0 0 100 120"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-4/5 text-muted-foreground/35"
                                    fill="currentColor"
                                    aria-hidden="true"
                                  >
                                    <circle cx="50" cy="38" r="22" />
                                    <path d="M0,120 C0,80 20,68 50,68 C80,68 100,80 100,120 Z" />
                                  </svg>
                                </div>
                              </div>
                              <p className="text-xs text-muted-foreground text-center mt-2">
                                {t.frameYourFace}
                              </p>
                            </div>
                          )}

                          <div className="space-y-3">
                            <button
                              onClick={() => cameraInputRef.current?.click()}
                              className="w-full p-4 bg-card border border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-colors flex items-center gap-4"
                            >
                              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                                <Camera className="w-6 h-6 text-primary" />
                              </div>
                              <div className="text-left">
                                <p className="font-medium text-foreground">{t.takePhoto}</p>
                                <p className="text-sm text-muted-foreground">{t.useCamera}</p>
                              </div>
                            </button>
                            <input
                              ref={cameraInputRef}
                              type="file"
                              accept="image/*"
                              capture="environment"
                              onChange={handleFileSelect}
                              className="hidden"
                            />

                            <button
                              onClick={() => fileInputRef.current?.click()}
                              className="w-full p-4 bg-card border border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-colors flex items-center gap-4"
                            >
                              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                                <Upload className="w-6 h-6 text-primary" />
                              </div>
                              <div className="text-left">
                                <p className="font-medium text-foreground">{t.uploadFile}</p>
                                <p className="text-sm text-muted-foreground">
                                  {t.uploadFileDesc(currentQuestion.documentConfig?.maxSizeMB ?? 10)}
                                </p>
                              </div>
                            </button>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept={currentQuestion.documentConfig?.acceptedFormats.join(",")}
                              onChange={handleFileSelect}
                              className="hidden"
                            />
                          </div>

                          {/* Drag & drop hint — desktop only (touch devices have no drag) */}
                          <p className="hidden md:block text-center text-xs text-muted-foreground mt-3">
                            {t.orDragAndDrop}
                          </p>
                        </div>

                        {/* Skip option lives OUTSIDE the drop zone so it can't be confused with an upload action */}
                        <button
                          onClick={() => {
                            if (!currentQuestion) return;
                            const qid = currentQuestion.id;
                            setSkipReason("");
                            setSkipDialog({
                              qid,
                              isDoc: true,
                              proceed: () => {
                                updateValue("__no_document__");
                                validateAndProceed("__no_document__");
                              },
                            });
                          }}
                          type="button"
                          className="w-full text-center min-h-[44px] py-2.5 mt-3 text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-4 decoration-muted-foreground/60 hover:decoration-foreground"
                        >
                          {t.noDocument}
                        </button>
                      </>
                    ) : (
                      <div>
                        {previewUrl ? (
                          <div className="relative aspect-[4/3] bg-secondary rounded-xl overflow-hidden mb-4">
                            <img
                              src={previewUrl}
                              alt={t.documentPreviewAlt}
                              className="w-full h-full object-contain"
                            />
                            <button
                              onClick={clearFile}
                              className="absolute top-3 right-3 w-8 h-8 bg-foreground/80 rounded-full flex items-center justify-center hover:bg-foreground transition-colors"
                              aria-label={t.removeDocumentAria}
                            >
                              <X className="w-4 h-4 text-background" />
                            </button>
                          </div>
                        ) : (
                          <div className="p-4 bg-secondary rounded-xl mb-4">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                                <FileImage className="w-6 h-6 text-primary" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-foreground truncate">
                                  {(pendingFile || (getCurrentValue() as File))?.name}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {formatFileSize(
                                    (pendingFile || (getCurrentValue() as File))?.size || 0,
                                  )}
                                </p>
                              </div>
                              <button
                                onClick={clearFile}
                                className="p-2 hover:bg-muted rounded-lg transition-colors"
                                aria-label={t.removeDocumentAria}
                              >
                                <X className="w-5 h-5 text-muted-foreground" />
                              </button>
                            </div>
                          </div>
                        )}
                        {ocrStatus !== 'error' && ocrStatus !== 'scanning' && (
                          <div className="flex items-center gap-2 text-success text-sm">
                            <CheckCircle2 className="w-4 h-4" />
                            <span>{t.documentUploaded}</span>
                          </div>
                        )}

                        {/* OCR scanning progress */}
                        {ocrStatus === "scanning" && (
                          <div className="mt-4 p-4 bg-primary/5 rounded-xl">
                            <div className="flex items-center gap-3 mb-2">
                              <ScanLine className="w-5 h-5 text-primary animate-pulse" />
                              <span className="text-sm font-medium text-foreground">
                                {t.verifyingDocument}
                              </span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                              <div
                                className="bg-primary h-2 rounded-full transition-all duration-300"
                                style={{ width: `${ocrProgress}%` }}
                              />
                            </div>
                          </div>
                        )}

                        {/* OCR extracted fields - editable */}
                        {ocrStatus === "success" &&
                          ocrResult &&
                          ocrResult.displayFields.length > 0 && (
                            <div className="mt-4 p-4 bg-primary/5 border border-primary/20 rounded-xl">
                              <div className="flex items-center gap-2 mb-1">
                                <CheckCircle2 className="w-5 h-5 text-primary" />
                                <span className="text-sm font-semibold text-foreground">
                                  {t.extractedInformation}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground mb-4">
                                {t.extractedInfoHelp}
                              </p>
                              <div className="space-y-3">
                                {ocrResult.displayFields.map((f) => {
                                  const rawValue = editableOcrFields[f.fieldId];
                                  const isDate = rawValue instanceof Date;
                                  // Pour le passeport d'une personne, la case lue est un écran de
                                  // cette personne : on retrouve sa question (et ses choix).
                                  const question = allQuestions[f.fieldId] ?? visibleQuestions.find((vq) => vq.id === f.fieldId);
                                  const isSelect = question?.type === 'select' && question?.options;

                                  return (
                                    <div key={f.fieldId}>
                                      <label className="text-xs text-muted-foreground mb-1 block">
                                        {f.label}
                                      </label>
                                      {isSelect ? (
                                        (question.options!.length > 20) ? (
                                          <CountryCombobox
                                            value={String(rawValue || "")}
                                            onValueChange={(val) => {
                                              setEditableOcrFields((prev) => ({ ...prev, [f.fieldId]: val }));
                                              setData((prev) => writeAnswer(prev, f.fieldId, val));
                                            }}
                                            searchPlaceholder={t.searchCountry}
                                            language={preferredLanguage}
                                          />
                                        ) : (
                                          <Select
                                            value={(() => {
                                              const raw = String(rawValue || "");
                                              const byName = question.options!.find((o) => o.name === raw);
                                              if (byName) return byName.code;
                                              return raw;
                                            })()}
                                            onValueChange={(code) => {
                                              setEditableOcrFields((prev) => ({ ...prev, [f.fieldId]: code }));
                                              setData((prev) => writeAnswer(prev, f.fieldId, code));
                                            }}
                                          >
                                            <SelectTrigger className="h-11">
                                              <SelectValue placeholder={t.selectPlaceholder} />
                                            </SelectTrigger>
                                            <SelectContent className="max-h-64">
                                              {question.options!.map((o) => (
                                                <SelectItem key={o.code} value={o.code}>
                                                  {getLocalizedOptionLabel(o, preferredLanguage)}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        )
                                      ) : isDate ? (
                                        <Popover>
                                          <PopoverTrigger asChild>
                                            <Button
                                              variant="outline"
                                              className="w-full h-11 justify-start text-left font-normal text-sm"
                                            >
                                              <CalendarIcon className="mr-2 h-4 w-4" />
                                              {formatDateLocalized(rawValue, preferredLanguage)}
                                            </Button>
                                          </PopoverTrigger>
                                          <PopoverContent className="w-auto p-0 bg-card" align="start">
                                            <Calendar
                                              mode="single"
                                              selected={toDate(rawValue)}
                                              onSelect={(date) => {
                                                if (date) {
                                                  setEditableOcrFields((prev) => ({ ...prev, [f.fieldId]: date }));
                                                  setData((prev) => writeAnswer(prev, f.fieldId, date));
                                                }
                                              }}
                                              initialFocus
                                              captionLayout="dropdown-buttons"
                                              fromYear={1920}
                                              toYear={new Date().getFullYear() + 15}
                                              className="p-3 pointer-events-auto"
                                              locale={getDateFnsLocale(preferredLanguage)}
                                            />
                                          </PopoverContent>
                                        </Popover>
                                      ) : (
                                        <Input
                                          value={String(rawValue || "")}
                                          onChange={(e) => {
                                            setEditableOcrFields((prev) => ({ ...prev, [f.fieldId]: e.target.value }));
                                            setData((prev) => writeAnswer(prev, f.fieldId, e.target.value));
                                          }}
                                          className="h-11"
                                        />
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                        {/* OCR error (subtle, non-blocking) */}
                        {ocrStatus === "error" && (
                          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                            <div className="flex items-start gap-2 mb-2">
                              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                              <span className="text-sm text-amber-900 leading-relaxed">
                                {ocrResult?.error === 'WRONG_DOCUMENT' && ocrResult.wrongDocumentFeedback
                                  ? ocrResult.wrongDocumentFeedback
                                  : ocrResult?.error === 'REQUIREMENTS_NOT_MET'
                                    ? (ocrResult.requirementsFeedback ?? t.requirementsNotMetDefault)
                                    : ocrResult?.error === 'NAME_MISMATCH' && ocrResult.nameMismatchFeedback
                                      ? ocrResult.nameMismatchFeedback
                                      : ocrHasRetriesLeft
                                        ? t.ocrRetryHint
                                        : t.ocrSkipHint}
                              </span>
                            </div>
                            {/* Attempt counter — clients thought they were permanently
                                stuck; showing progress toward "you can continue after
                                the last attempt" removes that dead-end feeling. */}
                            {ocrHasRetriesLeft && (
                              <div className="flex items-center justify-between gap-2 mb-2 pl-7">
                                <span className="text-xs font-medium text-amber-700">
                                  {t.ocrAttempt(ocrAttemptCount, ocrMaxAttempts)}
                                </span>
                                {ocrAttemptCount === ocrMaxAttempts - 1 && (
                                  <span className="text-xs text-amber-700/80">{t.ocrLastAttemptHint}</span>
                                )}
                              </div>
                            )}
                            {ocrHasRetriesLeft && (
                              <Button
                                variant="outline"
                                onClick={() => {
                                  clearFile();
                                  setTimeout(() => fileInputRef.current?.click(), 100);
                                }}
                                className="w-full min-h-[44px] mt-1 border-amber-300 hover:bg-amber-100"
                              >
                                <Upload className="w-4 h-4 mr-2" />
                                {t.ocrRetryButton}
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>


          {/* Why this matters */}
          {currentQuestion.whyImportant && (
            <Collapsible
              open={isWhyOpen}
              onOpenChange={setIsWhyOpen}
              className="mb-4"
            >
              <CollapsibleTrigger className="flex items-center gap-2 text-sm text-primary hover:underline">
                <ChevronDown
                  className={cn(
                    "w-4 h-4 transition-transform",
                    isWhyOpen && "rotate-180",
                  )}
                />
                {t.whyItMatters}
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 bg-secondary/50 rounded-xl text-sm text-muted-foreground mt-3">
                  <p className="mb-2 whitespace-pre-line">{currentQuestion.whyImportant.reason}</p>
                  {currentQuestion.whyImportant.consequence && (
                    <p className="text-foreground font-medium whitespace-pre-line">
                      {currentQuestion.whyImportant.consequence}
                    </p>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Inline "can't answer this question" — contextual, lives with the question */}
          {!reviewModeQuestions && !editingFromReview.current && currentQuestion?.type !== "document" && currentQuestion?.type !== "message" && (
            <button
              onClick={() => {
                if (isNavigating.current || !currentQuestion) return;
                const q = currentQuestion;
                setSkipReason("");
                setSkipDialog({
                  qid: q.id,
                  isDoc: false,
                  proceed: () => {
                    isNavigating.current = true;
                    // Save "__skipped__" marker so the dashboard knows the client saw it but couldn't answer
                    annulerLaSauvegardeEnAttente(); // ce qui était tapé ne doit pas repasser par-dessus le passage
                    if (annexeNonEnregistree.current === q.id) annexeNonEnregistree.current = null;
                    updateValue("__skipped__", { sansSauvegarde: true }); // l'envoi explicite suit
                    persistAnswer(q.id, "__skipped__", undefined, q.type, q.label, q.multiEntryFields);
                    setShowSuccess(true);
                    plusTard(() => setShowSuccess(false), 300);
                    plusTard(() => {
                      if (currentStepIndex < visibleQuestions.length - 1) {
                        setCurrentStepIndex((prev) => prev + 1);
                      } else {
                        setShowReview(true);
                      }
                      window.scrollTo({ top: 0, behavior: "smooth" });
                      releaseLockAfterRender.current = true;
                    }, 200);
                  },
                });
              }}
              type="button"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-4 decoration-muted-foreground/50 hover:decoration-foreground"
            >
              {t.unableToAnswer}
            </button>
          )}
        </div>

        {/* Push nav to viewport bottom on short pages, sit at content end on long pages */}
        <div className="flex-1 min-h-[16px]" />

        {/* Inline navigation — no longer fixed; doesn't obstruct the view */}
        {(() => {
          const docNotUploaded =
            currentQuestion.type === "document" &&
            !(Array.isArray(getCurrentValue())
              ? (getCurrentValue() as File[]).length > 0
              : getCurrentValue() || pendingFile);
          const travelEmpty =
            currentQuestion.type === "travel-history" &&
            !data.hasNoTravelHistory &&
            (!data.travelHistory || data.travelHistory.length === 0);
          // On NE bloque PAS le client pendant une analyse à plusieurs fichiers,
          // volontairement. Le faire imposerait plusieurs secondes d'attente par
          // document — six documents, six attentes — à quelqu'un pour qui tout
          // fonctionne, au nom de l'exactitude de NOTRE registre. L'exactitude
          // est obtenue autrement : une analyse non terminée s'enregistre
          // « non vérifié », ce qui est vrai, plutôt que « vérifié », qui était
          // faux. Le client garde sa fluidité, le cabinet garde une donnée juste.
          const continueDisabled =
            ocrStatus === "scanning" || docNotUploaded || travelEmpty;
          const continueHint =
            ocrStatus === "scanning"
              ? t.continueHintScanning
              : docNotUploaded
                ? t.continueHintUploadFirst
                : travelEmpty
                  ? t.continueHintAddTravel
                  : null;
          return (
            <nav className="mt-6 flex flex-col gap-2" aria-label={t.continue}>
              {continueHint && (
                <p
                  className="text-xs text-muted-foreground text-center"
                  aria-live="polite"
                >
                  {continueHint}
                </p>
              )}
              <div className="flex gap-3">
                {(currentStepIndex > 0 || editingFromReview.current) && (
                  <Button
                    variant="outline"
                    onClick={goBack}
                    className="h-14 px-6 text-base"
                  >
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    {editingFromReview.current ? t.review : t.back}
                  </Button>
                )}
                <Button
                  onClick={() => validateAndProceed()}
                  className={cn(
                    "h-14 text-lg font-medium flex-1",
                    currentStepIndex === 0 && !editingFromReview.current && "w-full",
                  )}
                  disabled={continueDisabled}
                >
                  {editingFromReview.current ? t.save : t.continue}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </nav>
          );
        })()}
      </div>

      {/* Skip-reason capture (free text) — appears when a client declines a
          question or a document, so the consultant learns WHY it's blank. */}
      <Dialog
        open={!!skipDialog}
        onOpenChange={(o) => {
          if (!o) {
            setSkipDialog(null);
            setSkipReason("");
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {skipDialog?.isDoc ? t.skipDocReasonTitle : t.skipReasonTitle}
            </DialogTitle>
            <DialogDescription>{t.skipReasonDescription}</DialogDescription>
          </DialogHeader>
          <Textarea
            value={skipReason}
            onChange={(e) => setSkipReason(e.target.value)}
            placeholder={t.skipReasonPlaceholder}
            className="min-h-[110px] text-base"
            autoFocus
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="ghost"
              onClick={() => {
                setSkipDialog(null);
                setSkipReason("");
              }}
            >
              {t.cancel}
            </Button>
            <Button onClick={confirmSkip} disabled={!skipReason.trim()}>
              {t.skipReasonConfirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

// Helper: checks whether a question has a non-empty answer in the data map.
// Optional questions (isRequired === false) are never considered "missing".
function isAnswered(q: Question, data: FlowData): boolean {
  if (q.isRequired === false) return true;
  const val = readAnswer(data, q.id);
  if (val === undefined || val === null || val === '') return false;
  if (Array.isArray(val) && val.length === 0) return false;
  return true;
}

// Review Screen Component
/**
 * Sous quelle clé voyage le mot laissé au cabinet.
 *
 * Le double soulignement la range avec les autres notes de service (comme le
 * motif d'une question passée) : elle ne compte pas comme une réponse et ne
 * cherche pas de question correspondante.
 */
export const CLE_NOTE = "__noteAuCabinet";
/** Assez pour un paragraphe de contexte, pas pour y coller un roman. */
export const MAX_NOTE = 2000;

interface ReviewScreenProps {
  data: FlowData;
  questions: Question[];
  onBack: () => void;
  onSubmit: (note: string) => void;
  onStartReviewMode: () => void;
  onEditQuestion: (questionId: string) => void;
  saveStatus?: SaveStatus;
  t: import("@/lib/ui-translations").UiTranslations;
  preferredLanguage?: string;
  onLanguageChange?: (lang: SupportedLanguage) => void;
  /** Same mark as the question screens — see UnifiedWizardProps. */
  brandMark?: string;
}

function ReviewScreen({
  data,
  questions,
  onBack,
  onSubmit,
  onStartReviewMode,
  onEditQuestion,
  saveStatus = 'idle',
  t,
  preferredLanguage,
  onLanguageChange,
  brandMark = "Formio",
}: ReviewScreenProps) {
  const [confirmed, setConfirmed] = useState(false);
  const [note, setNote] = useState(() => String(data[CLE_NOTE] ?? ""));
  const unansweredCount = questions.filter(q => !isAnswered(q, data)).length;

  const totalQuestions = questions.length;

  const getDisplayValue = (question: Question): string => {
    const value = readAnswer(data, question.id);
    if (!value && value !== false) return "—";

    if (value === "__skipped__") return t.unableToAnswer ?? t.notAnswered;
    if (value === "__no_document__" && question.type !== "document") return t.noDocument ?? t.documentNotAvailable;

    switch (question.type) {
      case "date":
        const d = toDate(value);
        return d ? formatDateLocalized(d, preferredLanguage) : String(value);
      case "select": {
        // Values are saved as codes; fall back to name lookup for old data
        const option = question.options?.find((o) => o.code === value) || question.options?.find((o) => o.name === value);
        const label = option ? getLocalizedOptionLabel(option, preferredLanguage) : String(value);
        const isAutre = option?.code === "autre" || value === "autre";
        if (isAutre) {
          const precision = readAnswer(data, question.id + "_autrePrecision");
          return precision ? t.otherPrecision(String(precision)) : label;
        }
        return label;
      }
      case "yes-no":
        return value === "oui" ? t.yes : t.no;
      case "checkbox": {
        // Choix multiples : lister les libellés cochés plutôt que « Accepté »
        if (isMultiChoiceCheckbox(question)) {
          const codes = asCheckboxCodes(value);
          if (codes.length === 0) return "—";
          return codes
            .map((code) => {
              const option =
                question.options?.find((o) => o.code === code) ||
                question.options?.find((o) => o.name === code);
              return option ? getLocalizedOptionLabel(option, preferredLanguage) : code;
            })
            .join(", ");
        }
        return value ? t.acceptedShort : "—";
      }
      case "document":
        if (value === "__no_document__") return t.noDocument;
        if (Array.isArray(value)) {
          const count = value.length;
          if (count === 0) return "—";
          return t.documentsUploaded(count);
        }
        return value instanceof File
          ? value.name
          : value
            ? t.documentProvided
            : "—";
      case "address": {
        const addr = value as any;
        if (!addr?.street) return "—";
        const countryName =
          COUNTRIES.find((c) => c.name === addr.country)?.name || COUNTRIES.find((c) => c.code === addr.country)?.name || addr.country;
        return `${addr.street}${addr.apt ? ", " + addr.apt : ""}, ${addr.city}, ${addr.province}, ${countryName} ${addr.postalCode}`;
      }
      case "phone": {
        const ph = value as any;
        if (!ph?.countryCode) return "—";
        return `${ph.countryCode} ${ph.number}`;
      }
      case "travel-history":
        return formatTravelHistoryForReview(
          data.travelHistory || [],
          data.hasNoTravelHistory || false,
          preferredLanguage,
        );
      case "multi-entry": {
        if (!Array.isArray(value) || value.length === 0) return "—";
        const entries = value as Record<string, string>[];
        return entries
          .map((entry, i) => {
            const parts =
              question.multiEntryFields
                ?.map((f) => {
                  const val = entry[f.key];
                  if (f.type === "select" && f.options) {
                    const matched = f.options.find((o) => o.code === val) || f.options.find((o) => o.name === val);
                    return matched ? getLocalizedOptionLabel(matched, preferredLanguage) : val;
                  }
                  return val;
                })
                .filter(Boolean) || [];
            return parts.join(" — ");
          })
          .join("\n");
      }
      default:
        return String(value);
    }
  };

  const handleSubmit = () => {
    if (confirmed) {
      onSubmit(note.trim().slice(0, MAX_NOTE));
    }
  };

  // Group by section
  const sections: {
    name: string;
    items: { questionId: string; label: string; value: string; isDocument: boolean; isMultiEntry: boolean; question: Question }[];
  }[] = [];
  let currentSectionName = "";

  questions.forEach((q) => {
    const sectionName = q.section || t.general;
    if (sectionName !== currentSectionName) {
      currentSectionName = sectionName;
      sections.push({ name: sectionName, items: [] });
    }
    sections[sections.length - 1].items.push({
      questionId: q.id,
      label: q.label,
      value: getDisplayValue(q),
      isDocument: q.type === "document",
      isMultiEntry: q.type === "multi-entry" && !!q.multiEntryFields,
      question: q,
    });
  });

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-xl mx-auto px-4 pt-4 pb-8 md:pb-12">
        {/* Brand + language switcher header on review screen */}
        <div className="flex justify-between items-center mb-3">
          <span className="text-sm font-semibold text-primary leading-none">
            {brandMark}
          </span>
          {onLanguageChange && preferredLanguage && (
            <LanguageSwitcher
              value={preferredLanguage}
              onChange={onLanguageChange}
            />
          )}
        </div>

        {/* Milestone Progress — review = past all sections */}
        <MilestoneProgress
          currentStepIndex={totalQuestions - 1}
          totalSteps={totalQuestions}
          currentSection={t.finalReview}
          stepInSection={0}
          totalInSection={1}
          saveStatus={saveStatus}
          t={t}
        />

        <div className="bg-card rounded-2xl shadow-card p-6 md:p-8">
          {/* Back button */}
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">{t.back}</span>
          </button>

          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
            {t.reviewTitle}
          </h1>
          <p className="text-muted-foreground mb-6">
            {t.reviewSubtitle}
          </p>

          {/* Review items grouped by section */}
          <div className="space-y-6 mb-6">
            {sections.map((section, sIdx) => (
              <div key={sIdx}>
                <h3 className="text-sm font-semibold text-primary mb-3">
                  {section.name}
                </h3>
                <div className="space-y-2">
                  {section.items.map((item, index) => {
                    // Multi-entry: render as labeled cards
                    if (item.isMultiEntry) {
                      const raw = data[item.questionId];
                      const entries = Array.isArray(raw) ? raw as Record<string, string>[] : undefined;
                      return (
                        <div key={index} className="py-2 border-b border-border/50 last:border-0">
                          <div className="flex items-center justify-between mb-2 px-2 -mx-2">
                            <span className="text-xs text-muted-foreground">{item.label}</span>
                            <button
                              type="button"
                              onClick={() => onEditQuestion(item.questionId)}
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          {entries && entries.length > 0 ? (
                            <div className="space-y-2">
                              {entries.map((entry, i) => (
                                <div key={i} className="rounded-lg border border-border bg-secondary/30 p-3 text-sm">
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                                    {(isRepeatGroup(item.question) ? rosterFields(item.question) : item.question.multiEntryFields)?.map((mef) => {
                                      let val = entry[mef.key];
                                      if (!val) return null;
                                      if (mef.options) {
                                        const matched = mef.options.find((o: any) => o.name === val) || mef.options.find((o: any) => o.code === val);
                                        val = matched ? getLocalizedOptionLabel(matched, preferredLanguage) : val;
                                      }
                                      return (
                                        <div key={mef.key}>
                                          <p className="text-xs text-muted-foreground">{mef.label}</p>
                                          <p className="text-sm font-medium text-foreground">{val}</p>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground px-2">—</p>
                          )}
                        </div>
                      );
                    }

                    // Default: single-value row
                    return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => onEditQuestion(item.questionId)}
                      aria-label={t.editFieldAria(item.label)}
                      className="group w-full flex justify-between items-start py-2 border-b border-border/50 last:border-0 cursor-pointer hover:bg-muted/40 active:bg-muted/60 rounded-lg px-2 -mx-2 transition-colors text-left"
                    >
                      <span className="text-xs text-muted-foreground max-w-[40%]">
                        {item.label}
                      </span>
                      <div className="flex items-center gap-2 max-w-[55%]">
                        <span
                          className={cn(
                            "text-sm font-medium text-foreground text-right break-words",
                            item.isDocument && "flex items-center gap-2",
                          )}
                        >
                          {item.isDocument && item.value !== "—" && (
                            <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0" />
                          )}
                          {item.value}
                        </span>
                        <Pencil className="w-3.5 h-3.5 text-muted-foreground shrink-0" aria-hidden="true" />
                      </div>
                    </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Block submission if there are unanswered questions */}
          {unansweredCount > 0 ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-medium text-amber-800 mb-1">
                {t.unansweredQuestions(unansweredCount)}
              </p>
              <p className="text-xs text-amber-700 mb-3">
                {t.unansweredQuestionsHelp}
              </p>
              <Button onClick={onStartReviewMode} className="w-full gap-2">
                {t.answerMissing}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <>
              {/* Un questionnaire ne prévoit jamais tout. Sans cet endroit, la
                  personne qui a une précision à donner n'a que le choix de la
                  taire ou de la glisser dans une réponse où elle n'a rien à
                  faire, et le cabinet la découvre au mauvais endroit. */}
              <div className="p-4 border border-border rounded-xl mb-6">
                <label htmlFor="note-au-cabinet" className="block text-sm font-medium text-foreground mb-1">
                  {t.noteTitle}
                </label>
                <p className="text-xs text-muted-foreground mb-3">{t.noteHelp}</p>
                <Textarea
                  id="note-au-cabinet"
                  value={note}
                  onChange={(e) => setNote(e.target.value.slice(0, MAX_NOTE))}
                  placeholder={t.notePlaceholder}
                  rows={4}
                  className="resize-y"
                />
                {note.length > MAX_NOTE - 200 && (
                  <p className="text-xs text-muted-foreground mt-2 tabular-nums">
                    {note.length} / {MAX_NOTE}
                  </p>
                )}
              </div>

              {/* Final confirmation */}
              <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl mb-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={confirmed}
                    onCheckedChange={(checked) => setConfirmed(checked as boolean)}
                    className="mt-0.5"
                  />
                  <span className="text-sm text-foreground leading-relaxed">
                    {t.finalConfirmation}
                  </span>
                </label>
              </div>

              {/* Submit button */}
              <Button
                onClick={handleSubmit}
                disabled={!confirmed}
                className="w-full h-14 text-lg font-medium"
              >
                {t.submitAnswers}
                <CheckCircle2 className="w-5 h-5 ml-2" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Powered by badge */}
      <div className="fixed bottom-4 left-4">
        <p className="text-xs text-muted-foreground/60">
          {t.poweredBy} <span className="font-medium text-primary">Formio</span>
        </p>
      </div>
    </div>
  );
}
